import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { toast } from 'react-toastify';
import { Room, Track } from 'livekit-client';
import { getPuzzlesForProvider, bookmarkPuzzle, removeBookmark, getBookmarkedPuzzles } from '../../../../../services/chessPuzzleService';

type UserRole = 'teacher' | 'student';
const PUZZLE_MODE = 'puzzle' as const;
const TEACHING_MODE = 'teaching' as const;
type ChessMode = typeof PUZZLE_MODE | typeof TEACHING_MODE;

// Type guard functions
function isTeachingMode(mode: ChessMode): mode is typeof TEACHING_MODE {
    return mode === TEACHING_MODE;
}

function isPuzzleMode(mode: ChessMode): mode is typeof PUZZLE_MODE {
    return mode === PUZZLE_MODE;
}

function isTeacher(role: UserRole): role is 'teacher' {
    return role === 'teacher';
}

function isStudent(role: UserRole): role is 'student' {
    return role === 'student';
}

interface ChessPuzzle {
    id: string;
    fen: string;
    moves: string;
    rating: number;
    
    themes: string[];
    description?: string;
    hint?: string;
    complexity?: 'beginner' | 'intermediate' | 'advanced';
    category?: string;
    source?: 'lichess' | 'custom' | 'local';
    lichessId?: string;
}

interface ChequeredChessProps {
    subscriptionId: string;
    subscriptionName: string;
    userRole?: UserRole;
    isInMeeting?: boolean; // Whether user is currently in a LiveKit meeting
    participantName?: string; // Name of the current participant
    room?: Room | null; // LiveKit room instance (passed from wrapper)
    disableTeachingMode?: boolean; // When true (e.g., in LiveKit), hide teaching and force puzzle
}

interface ChessMove {
    type: 'move' | 'puzzle' | 'position';
    from: string;
    to: string;
    san: string;
    fen: string;
    playerName: string;
    timestamp: number;
    puzzleId?: string;
}

interface StudentPosition {
    studentName: string;
    fen: string;
    lastMove: string;
    timestamp: number;
    completed?: boolean;
    totalMoves?: number;
}

const ChequeredChess: React.FC<ChequeredChessProps> = ({
    subscriptionId,
    // subscriptionName,  // Reserved for future use
    userRole,
    isInMeeting = false,
    participantName = 'Player',
    room = null,
    disableTeachingMode = false
}) => {
    const [game, setGame] = useState(new Chess());
    const [currentPuzzle, setCurrentPuzzle] = useState<ChessPuzzle | null>(null);
    const [puzzleProgress, setPuzzleProgress] = useState<string[]>([]);
    const [mainZoomLevel, setMainZoomLevel] = useState(100);
    const [boardWidthPx, setBoardWidthPx] = useState<number>(isStudent(userRole) ? 720 : 600);
    const boardInnerPaddingPx = 16; // matches p-4
    const effectiveBoardWidth = Math.max(240, boardWidthPx - 2 * boardInnerPaddingPx);
    const [studentZoomLevels, setStudentZoomLevels] = useState<{ [key: string]: number }>({}); // Per-student zoom levels
    const boardContainerRef = useRef<HTMLDivElement>(null);
    const screenShareTrackRef = useRef<MediaStreamTrack | null>(null);
    const canvasMirrorRef = useRef<HTMLCanvasElement | null>(null);
    const canvasMirrorIntervalRef = useRef<number | null>(null);
    const boardViewBroadcastIntervalRef = useRef<number | null>(null);
    const [isCorrectMove, setIsCorrectMove] = useState<boolean | null>(null);
    const [puzzleCompleted, setPuzzleCompleted] = useState(false);
    const [customFen, setCustomFen] = useState('');
    const [showFenInput, setShowFenInput] = useState(false);
    const [boardOrientation, setBoardOrientation] = useState<'white' | 'black'>('white');
    const [isBoardShared, setIsBoardShared] = useState(false);
    const [studentPositions, setStudentPositions] = useState<Map<string, StudentPosition>>(new Map());
    const [shareMode, setShareMode] = useState<'collaboration' | 'view'>('collaboration');
    const [showHint, setShowHint] = useState(false);
    const isStandaloneChequered = typeof window !== 'undefined' && window.location.pathname.includes('/chequered/');
    const initialMode: ChessMode = disableTeachingMode ? PUZZLE_MODE : (isStandaloneChequered ? TEACHING_MODE : PUZZLE_MODE);
    const [mode, setMode] = useState<ChessMode>(initialMode);
    const [originalMode, setOriginalMode] = useState<ChessMode>(initialMode);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedComplexity, setSelectedComplexity] = useState<'all' | 'beginner' | 'intermediate' | 'advanced'>('all');
    const [selectedCategory, setSelectedCategory] = useState<'all' | 'tactics' | 'endgame' | 'opening' | 'middlegame'>('all');
    const [showPuzzleCreator, setShowPuzzleCreator] = useState(false);
    const [customPuzzles, setCustomPuzzles] = useState<ChessPuzzle[]>([]);
    const [showBulkImport, setShowBulkImport] = useState(true);
    const [puzzleModeTab, setPuzzleModeTab] = useState<'library' | 'import' | 'create'>('library');
    const [firestorePuzzles, setFirestorePuzzles] = useState<ChessPuzzle[]>([]);
    const [loadingPuzzles, setLoadingPuzzles] = useState(false);
    const [bookmarkedPuzzleIds, setBookmarkedPuzzleIds] = useState<string[]>([]);
    const [newPuzzle, setNewPuzzle] = useState({
        description: '',
        hint: '',
        solution: '',
        complexity: 'beginner' as 'beginner' | 'intermediate' | 'advanced',
        category: 'tactics' as 'tactics' | 'endgame' | 'opening' | 'middlegame',
        themes: ''
    });
    const [teachingModeHistory, setTeachingModeHistory] = useState<string[]>([]);
    const [showPiecePicker, setShowPiecePicker] = useState(false);
    const [selectedPieceType, setSelectedPieceType] = useState<string | null>(null);
    const [isPieceSetupMode, setIsPieceSetupMode] = useState(false);
    const [selectedTrayPiece, setSelectedTrayPiece] = useState<{piece: string, color: string} | null>(null);
    
    // Drawing/Annotation state
    const [rightClickedSquares, setRightClickedSquares] = useState<{[square: string]: {backgroundColor: string}}>({});
    const [drawingColor, setDrawingColor] = useState<'green' | 'red' | 'blue' | 'yellow'>('green');
    const [drawingMode, setDrawingMode] = useState<'highlight' | 'arrow'>('highlight');
    const [arrowStart, setArrowStart] = useState<string | null>(null);
    const [interactionMode, setInteractionMode] = useState<'move' | 'select' | 'delete'>('move'); // Lichess-style interaction modes
    const [selectedSquares, setSelectedSquares] = useState<Set<string>>(new Set()); // For selection mode
    const [arrows, setArrows] = useState<Array<[string, string, string]>>([]);
    const [hintArrows, setHintArrows] = useState<Array<[string, string, string]>>([]);
    
    // ESC key handler to cancel selections
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && mode === TEACHING_MODE) {
                if (selectedTrayPiece || arrowStart) {
                    setSelectedTrayPiece(null);
                    setArrowStart(null);
                    toast.info('✅ Selection cancelled');
                }
            }
        };
        
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [mode, selectedTrayPiece, arrowStart]);

    // Load puzzles from Firestore
    useEffect(() => {
        const loadFirestorePuzzles = async () => {
            if (mode !== 'puzzle') return;
            
            setLoadingPuzzles(true);
            try {
                // Load puzzles with current filters
                const puzzles = await getPuzzlesForProvider('service-provider-id', {
                    complexity: selectedComplexity !== 'all' ? selectedComplexity : undefined,
                    category: selectedCategory !== 'all' ? selectedCategory : undefined,
                    searchQuery: searchQuery || undefined,
                    limit: 100
                });
                
                console.log(`📚 Loaded ${puzzles.length} puzzles from Firestore`);
                setFirestorePuzzles(puzzles);
                
                // Load bookmarks (only if we have valid user ID)
                if (participantName && participantName !== 'Player') {
                    const bookmarks = await getBookmarkedPuzzles('service-provider-id', participantName);
                    console.log(`⭐ Loaded ${bookmarks.length} bookmarks`);
                    setBookmarkedPuzzleIds(bookmarks);
                } else {
                    setBookmarkedPuzzleIds([]);
                }
            } catch (error) {
                console.error('Error loading puzzles:', error);
                // Don't show error for empty database
                setFirestorePuzzles([]);
                setBookmarkedPuzzleIds([]);
            } finally {
                setLoadingPuzzles(false);
            }
        };
        
        loadFirestorePuzzles();
    }, [mode, selectedComplexity, selectedCategory, searchQuery, participantName]);

    // Expanded puzzle library with hints and categories
    const samplePuzzles: ChessPuzzle[] = [
        // Beginner Puzzles
        {
            id: 'b1',
            fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p2Q/2B1P3/8/PPPP1PPP/RNB1K1NR w KQkq - 4 4',
            moves: 'h5f7 e8f7 c4d5',
            rating: 1200,
            themes: ['fork', 'sacrifice'],
            description: 'Scholar\'s Mate Pattern - Find the winning sacrifice!',
            hint: 'Look for a check that wins material. The king is exposed!',
            complexity: 'beginner',
            category: 'tactics'
        },
        {
            id: 'b2',
            fen: 'r1bqkbnr/pppp1ppp/2n5/4p3/2B1P3/5Q2/PPPP1PPP/RNB1K1NR w KQkq - 4 4',
            moves: 'f3f7',
            rating: 1100,
            themes: ['checkmate', 'tactics'],
            description: 'Scholar\'s Mate - Deliver checkmate in one!',
            hint: 'The f7 pawn is the weakest point in Black\'s position.',
            complexity: 'beginner',
            category: 'tactics'
        },
        // Intermediate Puzzles
        {
            id: 'i1',
            fen: 'r1bqk2r/ppp2ppp/2n5/3np3/1b2P3/2NP1N2/PPP2PPP/R1BQKB1R w KQkq - 0 6',
            moves: 'd3d4 d5f4 d4e5',
            rating: 1500,
            themes: ['pin', 'tactics'],
            description: 'Exploit the pin! Find the tactical blow.',
            hint: 'The knight on d5 is pinned. How can you exploit this?',
            complexity: 'intermediate',
            category: 'tactics'
        },
        {
            id: 'i2',
            fen: 'r2qkb1r/ppp2ppp/2n2n2/3pp3/2B1P3/2N2N2/PPPP1PPP/R1BQK2R w KQkq - 0 6',
            moves: 'c4f7 e8f7 c3d5',
            rating: 1550,
            themes: ['sacrifice', 'fork'],
            description: 'Knight fork after sacrifice',
            hint: 'A piece sacrifice can win the queen. Think about knight forks!',
            complexity: 'intermediate',
            category: 'tactics'
        },
        // Advanced Puzzles
        {
            id: 'a1',
            fen: '2kr3r/ppp2ppp/4p3/3pP3/3P4/2P5/PP3PPP/2KR3R b - - 0 14',
            moves: 'd8d4 c3d4 h8c8',
            rating: 1800,
            themes: ['endgame', 'rook'],
            description: 'Rook endgame - Find the winning sequence!',
            hint: 'Activate your rooks. The exposed king is the key.',
            complexity: 'advanced',
            category: 'endgame'
        },
        {
            id: 'a2',
            fen: '6k1/5ppp/8/8/8/2B5/5PPP/6K1 w - - 0 1',
            moves: 'c3f6 g8h8 f6g7',
            rating: 1900,
            themes: ['endgame', 'bishop'],
            description: 'Bishop and pawn endgame',
            hint: 'Zugzwang! Force the king into a bad position.',
            complexity: 'advanced',
            category: 'endgame'
        },
        // Opening Puzzles
        {
            id: 'o1',
            fen: 'rnbqkb1r/pppp1ppp/5n2/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq - 4 3',
            moves: 'f3e5',
            rating: 1300,
            themes: ['opening', 'tactics'],
            description: 'Petrov Defense Trap',
            hint: 'The center pawn is hanging. Or is it a trap?',
            complexity: 'beginner',
            category: 'opening'
        },
        {
            id: 'o2',
            fen: 'r1bqkbnr/pppp1ppp/2n5/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R b KQkq - 3 3',
            moves: 'f8c5',
            rating: 1250,
            themes: ['opening', 'development'],
            description: 'Italian Game - Best developing move',
            hint: 'Develop with tempo! Attack the weak f2 square.',
            complexity: 'beginner',
            category: 'opening'
        },
        // Multi-Step Puzzles
        {
            id: 'ms1',
            fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p2Q/2B1P3/8/PPPP1PPP/RNB1K1NR w KQkq - 4 4',
            moves: 'h5f7 e8f7 b4f8',
            rating: 1600,
            themes: ['sacrifice', 'queen', 'multi-step'],
            description: 'Three-move combination: Queen sacrifice → King check → Winning the game!',
            hint: 'Start with a queen sacrifice on f7. The king must take, then check with the bishop!',
            complexity: 'intermediate',
            category: 'tactics'
        },
        {
            id: 'ms2',
            fen: '2kr1b1r/pp1npppp/2p1bn2/q7/3PN3/1PN1BP2/P2Q1PPP/2KR1B1R w - - 0 12',
            moves: 'd4c6 b7c6 b3d5 c6d5 d2d5',
            rating: 1700,
            themes: ['fork', 'tactics', 'multi-step'],
            description: 'Five-move tactical sequence: Knight fork → Capture → Knight fork → Capture → Central control',
            hint: 'Start with a knight fork on c6. After the capture, fork again with the other knight!',
            complexity: 'intermediate',
            category: 'tactics'
        },
        {
            id: 'ms3',
            fen: 'r3k2r/Pppp1ppp/1b3nbN/nP6/BBP1P3/q4N2/Pp1P2PP/R2Q1RK1 w kq - 0 1',
            moves: 'a4b5 a3b4 c4c5 b4b5',
            rating: 1800,
            themes: ['endgame', 'pawn', 'multi-step'],
            description: 'Four-move pawn breakthrough: Create passed pawns and promote!',
            hint: 'Sacrifice the a-pawn to open lines. Then push the c-pawn to create passed pawns!',
            complexity: 'advanced',
            category: 'endgame'
        }
    ];

    // Combine all puzzle sources: Firestore (primary) + custom (imported in session) + samples (fallback)
    const allPuzzles = [...firestorePuzzles, ...customPuzzles, ...samplePuzzles];

    // Filter puzzles (Firestore already applies some filters, this is for client-side refinement)
    const filteredPuzzles = allPuzzles.filter(puzzle => {
        // Search filter
        if (searchQuery && !puzzle.description?.toLowerCase().includes(searchQuery.toLowerCase()) &&
            !puzzle.themes.some(theme => theme.toLowerCase().includes(searchQuery.toLowerCase()))) {
            return false;
        }
        
        // Complexity filter
        if (selectedComplexity !== 'all' && puzzle.complexity !== selectedComplexity) {
            return false;
        }
        
        // Category filter
        if (selectedCategory !== 'all' && puzzle.category !== selectedCategory) {
            return false;
        }
        
        return true;
    });

    // Resize observer to keep board width proportional without CSS transforms (avoids drag ghost issues)
    useEffect(() => {
        const el = boardContainerRef.current;
        if (!el) return;
        const update = () => {
            try {
                const w = el.clientWidth || 700;
                const target = isStudent(userRole) ? Math.min(800, Math.floor(w * 0.95)) : Math.min(700, Math.floor(w * 0.9));
                setBoardWidthPx(target);
            } catch {}
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => { try { ro.disconnect(); } catch {} };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userRole]);

    // Prevent any drag interactions for students ONLY in view mode to avoid piece ghosting
    useEffect(() => {
        const container = boardContainerRef.current || document.body;
        const prevent = (e: Event) => { e.preventDefault(); };
        if (isStudent(userRole) && shareMode === 'view') {
            container.addEventListener('dragstart', prevent, { passive: false });
            container.addEventListener('touchmove', prevent, { passive: false });
            return () => {
                container.removeEventListener('dragstart', prevent as any);
                container.removeEventListener('touchmove', prevent as any);
            };
        }
        return;
    }, [userRole, shareMode]);

    // Ensure drag image stays under cursor during drag (HTML5 setDragImage)
    useEffect(() => {
        const container = boardContainerRef.current;
        if (!container) return;
        // Handler applies only when pieces are draggable (not student view-only)
        const handleDragStart = (e: DragEvent) => {
            if (isStudent(userRole) && shareMode === 'view') return; // don't allow in view
            const target = e.target as HTMLElement | null;
            if (!target) return;
            // react-chessboard renders piece images inside squares
            if (target.tagName && target.tagName.toLowerCase() === 'img' && e.dataTransfer) {
                const img = target as HTMLImageElement;
                try {
                    // center the drag image so the cursor sits in the middle of the piece
                    const offsetX = Math.floor(img.width / 2);
                    const offsetY = Math.floor(img.height / 2);
                    e.dataTransfer.setDragImage(img, offsetX, offsetY);
                } catch {}
            }
        };
        container.addEventListener('dragstart', handleDragStart);
        return () => {
            container.removeEventListener('dragstart', handleDragStart);
        };
    }, [userRole, shareMode]);

    // Load a puzzle (just loads locally, doesn't share)
    const loadPuzzle = useCallback((puzzle: ChessPuzzle) => {
        const newGame = new Chess(puzzle.fen);
        setGame(newGame);
        setCurrentPuzzle(puzzle);
        setPuzzleProgress([]);
        setIsCorrectMove(null);
        setPuzzleCompleted(false);
        setShowHint(false);
        setHintArrows([]);
        setMode('puzzle');
        toast.info(`Puzzle loaded! ${puzzle.description}`);
    }, []);

    // Send chess move via LiveKit data channel
    const sendChessData = useCallback((data: ChessMove) => {
        if (room && isInMeeting) {
            try {
                const encoder = new TextEncoder();
                const payload = encoder.encode(JSON.stringify({
                    type: 'chess',
                    data
                }));
                room.localParticipant?.publishData(payload, { reliable: true });
                console.log('♟️ Sent chess data:', data);
            } catch (error) {
                console.error('Error sending chess data:', error);
                // Don't disconnect on error, just log it
            }
        }
    }, [room, isInMeeting]);

    // Check for active puzzle/teaching when joining (for late joiners)
    useEffect(() => {
        if (!room || !isInMeeting || userRole !== 'student') return;

        // Check teacher's metadata for active puzzle
        const checkForActiveContent = () => {
            room.remoteParticipants.forEach((participant) => {
                if (participant.metadata) {
                    try {
                        const metadata = JSON.parse(participant.metadata);
                        console.log('🔍 Checking participant metadata:', metadata);

                        // Handle active puzzle
                        if (metadata.activePuzzle) {
                            const puzzle = metadata.activePuzzle;
                            console.log('♟️ Late joiner: Loading active puzzle from metadata');

                            // Reset state first
                            setPuzzleProgress([]);
                            setIsCorrectMove(null);
                            setPuzzleCompleted(false);
                            setShowHint(false);
                            setHintArrows([]);

                            // Load student progress if available
                            if (metadata.studentProgress && Array.isArray(metadata.studentProgress)) {
                                const studentPositionsMap = new Map();
                                metadata.studentProgress.forEach(student => {
                                    studentPositionsMap.set(student.studentName, {
                                        studentName: student.studentName,
                                        fen: student.fen,
                                        lastMove: student.lastMove,
                                        timestamp: student.timestamp,
                                        completed: student.completed,
                                        totalMoves: student.totalMoves
                                    });
                                });
                                setStudentPositions(studentPositionsMap);
                                console.log('📊 Late joiner: Loaded student progress:', metadata.studentProgress.length, 'students');
                            } else {
                                setStudentPositions(new Map());
                            }

                            // Set mode and share mode
                            setMode('puzzle');
                            setShareMode('collaboration');
                            setIsBoardShared(true);

                            // Update puzzle and board state
                            setCurrentPuzzle(puzzle);
                            const newGame = new Chess(puzzle.fen);
                            setGame(newGame);

                            toast.info('♟️ Joined active puzzle session in collaboration mode!');
                            console.log('🎯 Puzzle loaded for late joiner:', puzzle);
                        }

                    } catch (error) {
                        console.error('Error parsing participant metadata:', error);
                    }
                }
            });
        };

        // Check immediately
        checkForActiveContent();

        // Also check when new participants join (teacher might join after student)
        room.on('participantConnected', checkForActiveContent);

        return () => {
            room.off('participantConnected', checkForActiveContent);
        };
    }, [room, isInMeeting, userRole]);

    // Receive chess moves from other participants
    useEffect(() => {
        if (!room || !isInMeeting) return;

        const handleDataReceived = (payload: Uint8Array, participant: any) => {
            try {
                const decoder = new TextDecoder();
                const message = JSON.parse(decoder.decode(payload));
                
                // Handle puzzle / board sharing from teacher
                if (message.type === 'chess' && message.action === 'PUZZLE_SHARED') {
                    // Only students receive the puzzle
                    if (userRole !== 'teacher') {
                        try {
                            const puzzle = message.puzzle;
                            if (!puzzle) {
                                console.error('Received puzzle share without puzzle data');
                                return;
                            }

                            // Handle teaching board shared as puzzle
                            if (puzzle.category === 'teaching') {
                                console.log('📚 Student received teaching board as puzzle:', puzzle);
                                setShareMode('collaboration');
                                setMode('teaching');
                                setIsBoardShared(true);
                                
                                const newGame = new Chess(puzzle.fen);
                                setGame(newGame);
                                setCurrentPuzzle(null);
                                setPuzzleProgress([]);
                                setIsCorrectMove(null);
                                setPuzzleCompleted(false);
                                setShowHint(false);
                                setHintArrows([]);
                                
                                toast.success('📚 Teacher shared their board - watch their moves in real-time!');
                                return;
                            }

                            // Regular puzzle handling - always collaboration mode
                            console.log('🤝 Student received puzzle in collaboration mode:', message);

                            // Set states in a specific order
                            setShareMode('collaboration');
                            setMode('puzzle');
                            setIsBoardShared(true);
                            
                            // Then set game state
                            const newGame = new Chess(puzzle.fen);
                            setGame(newGame);
                            setCurrentPuzzle(puzzle);
                            setPuzzleProgress([]);
                            setIsCorrectMove(null);
                            setPuzzleCompleted(false);
                            setShowHint(false);
                            setHintArrows([]);
                            
                            // Show collaboration mode toast
                            console.log('🤝 Student switched to collaboration mode');
                            toast.success(`♟️ Collaboration Mode: Solve "${puzzle.description || 'this puzzle'}"`);
                        } catch (error) {
                            console.error('Error handling puzzle share:', error);
                            toast.error('Failed to load shared puzzle - please ask teacher to rebroadcast');
                        }
                    }
                    return;
                }

                // Ignore board-view broadcasting; we only support puzzle mode now
                if (message.type === 'chess' && (message.action === 'BOARD_VIEW_SHARED' || message.action === 'BOARD_VIEW_STOPPED')) {
                    return;
                }
                
                // Handle student moves (only teacher receives these)
                if (message.type === 'chess' && message.action === 'STUDENT_MOVE') {
                    // Only teacher processes student moves
                    if (userRole === 'teacher') {
                        setStudentPositions(prev => {
                            const newMap = new Map(prev);
                            const existing = newMap.get(message.playerName);
                            newMap.set(message.playerName, {
                                studentName: message.playerName,
                                fen: message.fen,
                                lastMove: message.san,
                                timestamp: message.timestamp,
                                completed: existing?.completed || false,
                                totalMoves: (existing?.totalMoves || 0) + 1 // Increment move count
                            });

                            // Update metadata for late joiners with current student progress
                            setTimeout(async () => {
                                try {
                                    const metadata = {
                                        activePuzzle: currentPuzzle ? {
                                            ...currentPuzzle,
                                            shareMode: 'collaboration'
                                        } : null,
                                        studentProgress: Array.from(newMap.entries()).map(([name, data]) => ({
                                            studentName: name,
                                            fen: data.fen,
                                            lastMove: data.lastMove,
                                            timestamp: data.timestamp,
                                            completed: data.completed,
                                            totalMoves: data.totalMoves
                                        })),
                                        timestamp: Date.now()
                                    };
                                    await room.localParticipant?.setMetadata(JSON.stringify(metadata));
                                    console.log('📝 Updated metadata with student progress for late joiners');
                                } catch (error) {
                                    console.warn('Could not update metadata with student progress:', error);
                                }
                            }, 100); // Small delay to ensure state is updated

                            return newMap;
                        });
                        console.log(`♟️ Student ${message.playerName} made move: ${message.san}`);
                    }
                    return;
                }
                
                // Handle puzzle completion (only teacher receives these)
                if (message.type === 'chess' && message.action === 'PUZZLE_COMPLETED') {
                    if (userRole === 'teacher') {
                        setStudentPositions(prev => {
                            const newMap = new Map(prev);
                            const existing = newMap.get(message.playerName);
                            if (existing) {
                                newMap.set(message.playerName, {
                                    ...existing,
                                    completed: true,
                                    totalMoves: message.totalMoves,
                                    timestamp: message.timestamp
                                });

                                // Update metadata for late joiners with current student progress
                                setTimeout(async () => {
                                    try {
                                        const metadata = {
                                            activePuzzle: currentPuzzle ? {
                                                ...currentPuzzle,
                                                shareMode: 'collaboration'
                                            } : null,
                                            studentProgress: Array.from(newMap.entries()).map(([name, data]) => ({
                                                studentName: name,
                                                fen: data.fen,
                                                lastMove: data.lastMove,
                                                timestamp: data.timestamp,
                                                completed: data.completed,
                                                totalMoves: data.totalMoves
                                            })),
                                            timestamp: Date.now()
                                        };
                                        await room.localParticipant?.setMetadata(JSON.stringify(metadata));
                                        console.log('📝 Updated metadata with puzzle completion for late joiners');
                                    } catch (error) {
                                        console.warn('Could not update metadata with puzzle completion:', error);
                                    }
                                }, 100); // Small delay to ensure state is updated
                            }
                            return newMap;
                        });
                        toast.success(`🎉 ${message.playerName} completed the puzzle!`, {
                            position: 'top-right',
                            autoClose: 3000
                        });
                        console.log(`✅ Student ${message.playerName} completed puzzle in ${message.totalMoves} moves`);
                    }
                    return;
                }
                
                // Handle puzzle share in collaboration mode
                if (message.type === 'chess' && message.action === 'PUZZLE_SHARED' && userRole === 'student') {
                    console.log('📚 Student received puzzle share:', message);
                    
                    try {
                        if (!message.puzzle) {
                            console.error('Received puzzle share without puzzle data');
                            return;
                        }

                        // Reset all state first
                        setPuzzleProgress([]);
                        setIsCorrectMove(null);
                        setPuzzleCompleted(false);
                        setShowHint(false);
                        setHintArrows([]);
                        setStudentPositions(new Map());
                        
                        // Set mode and share mode
                        setMode('puzzle');
                        setShareMode('collaboration');
                        setIsBoardShared(true);
                        
                        // Update puzzle and board state
                        setCurrentPuzzle(message.puzzle);
                        const newGame = new Chess(message.puzzle.fen);
                        setGame(newGame);
                        
                        toast.success(`🤝 Solve puzzle: "${message.puzzle.description || 'this puzzle'}"`);
                        console.log('🎯 Puzzle loaded:', message.puzzle);
                    } catch (error) {
                        console.error('Error handling board share:', error);
                        toast.error('Failed to load shared board - please ask teacher to rebroadcast');
                    }
                    return;
                }
                
                // Handle stop broadcast by teacher (both puzzle and board view)
                if (message.type === 'chess' && message.action === 'PUZZLE_STOPPED') {
                    if (userRole === 'student') {
                        // Clear student's puzzle
                        setCurrentPuzzle(null);
                        setGame(new Chess());
                        setPuzzleProgress([]);
                        setIsCorrectMove(null);
                        setPuzzleCompleted(false);
                        toast.info('Teacher stopped the puzzle session');
                    }
                    return;
                }

                if (message.type === 'chess' && message.action === 'BOARD_VIEW_STOPPED') {
                    if (userRole === 'student') {
                        setIsBoardShared(false);
                        setGame(new Chess());
                        setShareMode('collaboration');
                        toast.info('Teacher stopped board view');
                    }
                    return;
                }
                
                if (message.type === 'chess') {
                    const chessData: ChessMove = message.data;
                    console.log('♟️ Received chess data from', participant.name, ':', chessData);
                    
                    // If teacher shared a puzzle/position (legacy support)
                    if (chessData.type === 'puzzle' && userRole !== 'teacher') {
                        const newGame = new Chess(chessData.fen);
                        setGame(newGame);
                        setPuzzleProgress([]);
                        setIsCorrectMove(null);
                        setPuzzleCompleted(false);
                        toast.info(`📢 Teacher shared: ${chessData.puzzleId || 'Custom position'}`);
                    }
                    
                    // If teacher is broadcasting board view, update student board with latest FEN
                    if (chessData && chessData.type === 'position' && userRole !== 'teacher') {
                        try {
                            if (shareMode === 'view') {
                                const newGame = new Chess((chessData as any).fen);
                                setGame(newGame);
                            }
                        } catch (e) {
                            console.warn('Could not update board view position:', e);
                        }
                    }

                    // If student made a move (teacher receives all student moves) - legacy
                    if (chessData.type === 'move' && userRole === 'teacher') {
                        setStudentPositions(prev => {
                            const newMap = new Map(prev);
                            newMap.set(chessData.playerName, {
                                studentName: chessData.playerName,
                                fen: chessData.fen,
                                lastMove: chessData.san,
                                timestamp: chessData.timestamp
                            });
                            return newMap;
                        });
                    }
                }
            } catch (error) {
                console.error('Error parsing chess data:', error);
            }
        };

        room.on('dataReceived', handleDataReceived);
        
        return () => {
            room.off('dataReceived', handleDataReceived);
        };
    }, [room, isInMeeting, userRole]);

    // Late join/open via teacher metadata: load board view or active puzzle
    useEffect(() => {
        if (!room || !isInMeeting || userRole === 'teacher') return;

        const checkForTeacherMetadata = async () => {
            try {
                const participants = Array.from(room.participants.values());
                const teacher = participants.find((p: any) => p.isSpeaker || p.permissions?.canPublish);
                const metaRaw = teacher?.metadata || room.localParticipant?.metadata;
                if (!metaRaw) return;
                const meta = JSON.parse(metaRaw);
                if (meta?.boardView?.fen) {
                    setShareMode('view');
                    setMode('teaching');
                    setIsBoardShared(true);
                    setGame(new Chess(meta.boardView.fen));
                    console.log('📥 Loaded board view from metadata');
                } else if (meta?.activePuzzle?.fen) {
                    setShareMode('collaboration');
                    setMode('puzzle');
                    setIsBoardShared(true);
                    setCurrentPuzzle(meta.activePuzzle);
                    setGame(new Chess(meta.activePuzzle.fen));
                    console.log('📥 Loaded puzzle from metadata');
                }
            } catch (err) {
                console.warn('Metadata parse failed', err);
            }
        };

        checkForTeacherMetadata();
        room.on('participantConnected', checkForTeacherMetadata);
        return () => room.off('participantConnected', checkForTeacherMetadata);
    }, [room, isInMeeting, userRole]);

    // Load first puzzle on mount (only if NOT in meeting OR if teacher)
    useEffect(() => {
        // Students in meeting wait for teacher to share puzzle
        if (isInMeeting && userRole !== 'teacher') {
            return; // Don't auto-load for students in meetings
        }
        loadPuzzle(samplePuzzles[0]);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle piece drop
    const onDrop = (sourceSquare: string, targetSquare: string) => {
        // In view mode students cannot move
        if (userRole === 'student' && shareMode === 'view') {
            return false;
        }
        if (!currentPuzzle) return false;
        
        // Students can't move after completing puzzle
        if (puzzleCompleted && userRole === 'student') {
            toast.info('Puzzle already solved! Waiting for next puzzle...');
            return false;
        }
        
        // Teacher can always move in teaching mode
        if (puzzleCompleted && userRole === 'teacher' && mode === TEACHING_MODE) {
            // Allow teacher to continue in teaching mode
        } else if (puzzleCompleted) {
            return false;
        }

        const gameCopy = new Chess(game.fen());
        const move = gameCopy.move({
            from: sourceSquare,
            to: targetSquare,
            promotion: 'q' // Always promote to queen for simplicity
        });

        // Invalid move
        if (move === null) return false;

        // Get expected moves
        const expectedMoves = currentPuzzle.moves.split(' ');
        const currentMoveIndex = puzzleProgress.length;

        // Check if this is the correct move
        const isCorrect = (move.san === expectedMoves[currentMoveIndex] || 
            `${sourceSquare}${targetSquare}` === expectedMoves[currentMoveIndex]);

        if (isCorrect) {
            // Send correct move to teacher in real-time
            if (isInMeeting && room && userRole === 'student') {
                const moveData = {
                    type: 'chess',
                    action: 'STUDENT_MOVE',
                    from: sourceSquare,
                    to: targetSquare,
                    san: move.san,
                    fen: gameCopy.fen(),
                    playerName: participantName,
                    timestamp: Date.now(),
                    puzzleId: currentPuzzle.id,
                    isCorrect: true,
                    moveNumber: currentMoveIndex + 1
                };
                
                try {
                    const encoder = new TextEncoder();
                    const payload = encoder.encode(JSON.stringify(moveData));
                    room.localParticipant?.publishData(payload, { reliable: true });
                    console.log(`♟️ Student move sent to teacher: ${move.san} (correct)`);
                } catch (error) {
                    console.error('Error sending student move:', error);
                }
            }
            
            setGame(gameCopy);
            setPuzzleProgress([...puzzleProgress, move.san]);
            setIsCorrectMove(true);
            

            // Check if puzzle is completed
            if (currentMoveIndex + 1 >= expectedMoves.length) {
                setPuzzleCompleted(true);
                toast.success('🎉 Puzzle solved! Great job!');
                
                // Notify teacher that student completed the puzzle
                if (isInMeeting && room && userRole === 'student') {
                    const completionData = {
                        type: 'chess',
                        action: 'PUZZLE_COMPLETED',
                        playerName: participantName,
                        puzzleId: currentPuzzle.id,
                        timestamp: Date.now(),
                        totalMoves: puzzleProgress.length + 1
                    };
                    
                    try {
                        const encoder = new TextEncoder();
                        const payload = encoder.encode(JSON.stringify(completionData));
                        room.localParticipant?.publishData(payload, { reliable: true });
                        console.log('♟️ Puzzle completion sent to teacher');
                    } catch (error) {
                        console.error('Error sending completion:', error);
                    }
                }
            } else {
                toast.success('✅ Correct move!');
                
                // Make computer's response move after a delay
                setTimeout(() => {
                    if (currentMoveIndex + 1 < expectedMoves.length) {
                        const nextMove = expectedMoves[currentMoveIndex + 1];
                        const computerMove = gameCopy.move(nextMove);
                        if (computerMove) {
                            setGame(new Chess(gameCopy.fen()));
                            setPuzzleProgress(prev => [...prev, computerMove.san]);
                        }
                    }
                }, 500);
            }
        } else {
            setIsCorrectMove(false);
            toast.error('❌ Incorrect move. Try again!');
            return false;
        }

        return true;
    };

    // Reset puzzle
    const resetPuzzle = () => {
        if (currentPuzzle) {
            loadPuzzle(currentPuzzle);
        }
    };

    // Load custom position from FEN
    const loadCustomPosition = () => {
        try {
            const newGame = new Chess(customFen);
            setGame(newGame);
            setCurrentPuzzle(null);
            setPuzzleCompleted(false);
            setPuzzleProgress([]);
            setShowFenInput(false);
            toast.success('Custom position loaded!');
        } catch (error) {
            toast.error('Invalid FEN string!');
        }
    };

    // Share puzzle in collaboration mode
    const shareBoardWithStudents = async () => {
        if (!room || !isInMeeting) {
            toast.error('Not in a live meeting');
            return;
        }

        if (userRole !== 'teacher') {
            toast.error('Only teachers can share puzzles');
            return;
        }

        if (!currentPuzzle) {
            toast.error('Please load a puzzle first');
            return;
        }

        if (mode !== 'puzzle') {
            toast.error('Share is only available in puzzle mode');
            return;
        }

        // Set local state
        setOriginalMode('puzzle');
        setIsBoardShared(true);
        setShareMode('collaboration');
        setStudentPositions(new Map());

        // Prepare share data
        const shareData = {
            type: 'chess',
            action: 'PUZZLE_SHARED',
            fen: currentPuzzle.fen,
            shareMode: 'collaboration',
            playerName: participantName,
            timestamp: Date.now(),
            puzzle: {
                id: currentPuzzle.id,
                fen: currentPuzzle.fen,
                moves: currentPuzzle.moves,
                description: currentPuzzle.description,
                rating: currentPuzzle.rating,
                themes: currentPuzzle.themes,
                complexity: currentPuzzle.complexity
            }
        };

        // Send the share data
        try {
            const encoder = new TextEncoder();
            const payload = encoder.encode(JSON.stringify(shareData));
            await room.localParticipant?.publishData(payload, { reliable: true });
            console.log('📢 Share data sent:', shareData);

            // Set metadata for late joiners
            try {
                const metadata = {
                    activePuzzle: {
                        ...currentPuzzle,
                        shareMode: 'collaboration'
                    },
                    studentProgress: [], // Initially empty, will be updated as students progress
                    timestamp: Date.now()
                };
                await room.localParticipant?.setMetadata(JSON.stringify(metadata));
                console.log('📝 Initial metadata set for late joiners');
            } catch (error) {
                console.warn('Could not set metadata:', error);
            }
        } catch (error) {
            console.error('Failed to share puzzle:', error);
            toast.error('Failed to share puzzle - please try again');
            return;
        }

        const puzzleData = {
            type: 'chess',
            action: 'PUZZLE_SHARED',
            puzzle: {
                id: currentPuzzle.id,
                fen: currentPuzzle.fen,
                moves: currentPuzzle.moves,
                description: currentPuzzle.description,
                rating: currentPuzzle.rating,
                themes: currentPuzzle.themes,
                complexity: currentPuzzle.complexity,
                category: 'puzzle'
            },
            shareMode: 'collaboration', // Always collaboration mode for puzzles
            timestamp: Date.now()
        };

        try {
            // Save the current mode before sharing (for restoration when stopping)
            setOriginalMode(mode);

            // Set local state first before broadcasting
            setIsBoardShared(true);
            setStudentPositions(new Map());

            // Function to send a single broadcast
            const sendBroadcast = async (attempt = 1) => {
                try {
                    const encoder = new TextEncoder();
                    const payload = encoder.encode(JSON.stringify(puzzleData));
                    await room.localParticipant?.publishData(payload, { reliable: true });
                    console.log(`📢 Broadcast ${attempt} sent successfully`);
                    return true;
                } catch (error) {
                    console.warn(`❌ Broadcast ${attempt} failed:`, error);
                    return false;
                }
            };

            // Wait for state to settle, then broadcast
            setTimeout(async () => {
                // Send initial broadcast
                const firstTry = await sendBroadcast(1);
                
                // Wait and send confirmation broadcast
                await new Promise(resolve => setTimeout(resolve, 800));
                const secondTry = await sendBroadcast(2);
                
                // If both failed, try one more time
                if (!firstTry && !secondTry) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    const finalTry = await sendBroadcast(3);
                    
                    if (!finalTry) {
                        console.error('❌ All broadcast attempts failed');
                        toast.error('Share failed - please try rebroadcast');
                        return;
                    }
                }
        try {
            const encoder = new TextEncoder();
            const payload = encoder.encode(JSON.stringify(puzzleData));
            
                    // Send via data channel with retry mechanism
                    let retryCount = 0;
                    const maxRetries = 3;

                    while (retryCount < maxRetries) {
                        try {
            await room.localParticipant?.publishData(payload, { reliable: true });
                            console.log('♟️ Puzzle data published successfully');
                            break;
                        } catch (publishError) {
                            retryCount++;
                            console.log(`♟️ Publish attempt ${retryCount} failed:`, publishError);

                            if (retryCount < maxRetries) {
                                await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
                            } else {
                                console.error('♟️ Failed to publish puzzle after all retries');
                                toast.error('Puzzle shared but may not reach all participants - use rebroadcast button');
                            }
                        }
                    }

                    // Set metadata for late joiners - only if teacher in puzzle mode
                    if (userRole === 'teacher' && mode === PUZZLE_MODE && currentPuzzle) {
                        try {
                            const metadata = {
                                activePuzzle: {
                                    id: currentPuzzle.id,
                                    fen: currentPuzzle.fen,
                                    moves: currentPuzzle.moves,
                                    description: currentPuzzle.description,
                                    rating: currentPuzzle.rating,
                                    themes: currentPuzzle.themes,
                                    complexity: currentPuzzle.complexity,
                                    category: currentPuzzle.category,
                                    shareMode: 'collaboration'
                                },
                                studentProgress: Array.from(studentPositions.entries()).map(([name, data]) => ({
                                    studentName: name,
                                    fen: data.fen,
                                    lastMove: data.lastMove,
                                    timestamp: data.timestamp,
                                    completed: data.completed,
                                    totalMoves: data.totalMoves
                                })),
                                timestamp: Date.now()
                            };

                            await room.localParticipant?.setMetadata(JSON.stringify(metadata));
                            console.log('📝 Teacher updated metadata for puzzle late joiners:', metadata);
                        } catch (metadataError) {
                            console.warn('Could not update metadata for late joiners:', metadataError);
                            // Continue anyway - real-time sharing still works
                        }
                    } else {
                        console.log(`🎓 Skipping metadata update - ${userRole} in ${mode} mode`);
                    }
            
            const modeText = 'Collaboration Mode';
                    const actionText = mode === TEACHING_MODE ? 'Teaching board' : 'Puzzle';
                    toast.success(`📢 ${actionText} shared in ${modeText}!`);
        } catch (error) {
                    console.error('Error in puzzle publish retry:', error);
                }
            }, 500); // Wait 500ms for state to settle

            console.log(`♟️ Board sharing initiated, mode: ${mode}`);
        } catch (error) {
            console.error('Error sharing board:', error);
            toast.error('Failed to share board');
        }
    };

    // Share teacher board in view-only mode
    const shareBoardViewMode = async () => {
        if (!room || !isInMeeting) {
            toast.error('Not in a live meeting');
            return;
        }

        try {
            setOriginalMode(mode);
            // Force teaching mode for view-only broadcast
            setMode(TEACHING_MODE);
            setCurrentPuzzle(null);
            setIsBoardShared(true);
            setShareMode('view');

            const element = boardContainerRef.current;
            if (!element) throw new Error('Chequered container not found');

            // 1) Prefer Element Region Capture (closest to screenshare, cropped to board)
            let published = false;
            try {
                const supportsCropTarget = typeof (window as any).CropTarget !== 'undefined';
                // Capture current tab to avoid infinite mirror when the board is in-app
                const stream: MediaStream = await (navigator.mediaDevices as any).getDisplayMedia({
                    video: { preferCurrentTab: true },
                    audio: false
                });
                const track = stream.getVideoTracks()[0];
                if (track && supportsCropTarget && typeof (track as any).cropTo === 'function') {
                    const cropTarget = await (window as any).CropTarget.fromElement(element);
                    await (track as any).cropTo(cropTarget);
                }
                screenShareTrackRef.current = track;
                await room.localParticipant.publishTrack(track, { name: 'chequered-view' as any, source: Track.Source.ScreenShare });
                
                // Notify students to open board in view mode
                try {
                    const encoder = new TextEncoder();
                    const viewData = {
                        type: 'chess',
                        action: 'BOARD_VIEW_SHARED',
                        fen: game.fen(),
                        shareMode: 'view',
                        timestamp: Date.now()
                    };
                    const payload = encoder.encode(JSON.stringify(viewData));
                    await room.localParticipant?.publishData(payload, { reliable: true });
                } catch (_) {}

                // Start periodic FEN broadcast
                if (boardViewBroadcastIntervalRef.current) {
                    window.clearInterval(boardViewBroadcastIntervalRef.current);
                }
                boardViewBroadcastIntervalRef.current = window.setInterval(async () => {
                    try {
                        const encoder = new TextEncoder();
                        const pos = {
                            type: 'chess',
                            data: {
                                type: 'position',
                                fen: game.fen(),
                                playerName: participantName,
                                timestamp: Date.now()
                            }
                        } as any;
                        const payload = encoder.encode(JSON.stringify(pos));
                        await room.localParticipant?.publishData(payload, { reliable: false });
                    } catch (_) {}
                }, 2000);

                published = true;
                toast.success('📺 Chequered view shared');
            } catch (e) {
                console.warn('Element Capture not available, falling back to canvas mirror', e);
            }

            // 2) Fallback to canvas streaming (no popup). Dynamically import html2canvas.
            if (!published) try {
                const html2canvasMod = await import(/* @vite-ignore */ 'html2canvas');
                const html2canvas = html2canvasMod.default || (html2canvasMod as any);
                // Prepare mirror canvas
                const { width, height } = element.getBoundingClientRect();
                const mirror = document.createElement('canvas');
                mirror.width = Math.max(1, Math.floor(width));
                mirror.height = Math.max(1, Math.floor(height));
                canvasMirrorRef.current = mirror;

                const fps = 8; // balance CPU and quality
                const tick = async () => {
                    try {
                        const snap = await html2canvas(element, {
                            backgroundColor: null,
                            scale: 1,
                            useCORS: true,
                            onclone: undefined
                        });
                        const mctx = mirror.getContext('2d');
                        if (mctx) {
                            mctx.clearRect(0, 0, mirror.width, mirror.height);
                            mctx.drawImage(snap, 0, 0, mirror.width, mirror.height);
                        }
                    } catch (e) {
                        // continue; next frame will try again
                    }
                };

                // Prime first frame
                await tick();

                // Start capture stream from mirror
                if (typeof (mirror as any).captureStream === 'function') {
                    const stream: MediaStream = (mirror as any).captureStream(fps);
                    const track = stream.getVideoTracks()[0];
                    screenShareTrackRef.current = track;
                    await room.localParticipant.publishTrack(track, { name: 'chequered-view' as any, source: Track.Source.ScreenShare });
                    // Schedule periodic redraws
                    canvasMirrorIntervalRef.current = window.setInterval(tick, Math.floor(1000 / fps));
                    published = true;
                    // Notify students to open board in view mode
                    try {
                        const encoder = new TextEncoder();
                        const viewData = {
                            type: 'chess',
                            action: 'BOARD_VIEW_SHARED',
                            fen: game.fen(),
                            shareMode: 'view',
                            timestamp: Date.now()
                        };
                        const payload = encoder.encode(JSON.stringify(viewData));
                        await room.localParticipant?.publishData(payload, { reliable: true });
                    } catch (_) {}
                    // Start periodic FEN broadcast to keep students in sync
                    if (boardViewBroadcastIntervalRef.current) {
                        window.clearInterval(boardViewBroadcastIntervalRef.current);
                    }
                    boardViewBroadcastIntervalRef.current = window.setInterval(async () => {
                        try {
                            const encoder = new TextEncoder();
                            const pos = {
                                type: 'chess',
                                data: {
                                    type: 'position',
                                    fen: game.fen(),
                                    playerName: participantName,
                                    timestamp: Date.now()
                                }
                            } as any;
                            const payload = encoder.encode(JSON.stringify(pos));
                            await room.localParticipant?.publishData(payload, { reliable: false });
                        } catch (_) {}
                    }, 2000);
                    toast.success('📺 Chequered view shared');
                }
            } catch (e) {
                console.warn('html2canvas unavailable, falling back to browser share picker', e);
            }

            // 3) Last fallback: regular display capture without crop
            if (!published) {
                const stream = await (navigator.mediaDevices as any).getDisplayMedia({ video: true, audio: false });
                const track: MediaStreamTrack | undefined = stream.getVideoTracks()?.[0];
                if (!track) throw new Error('No video track from display media');
                screenShareTrackRef.current = track;
                await room.localParticipant.publishTrack(track, { name: 'chequered-view' as any, source: Track.Source.ScreenShare });
                try {
                    const encoder = new TextEncoder();
                    const viewData = { type: 'chess', action: 'BOARD_VIEW_SHARED', fen: game.fen(), shareMode: 'view', timestamp: Date.now() } as any;
                    const payload = encoder.encode(JSON.stringify(viewData));
                    await room.localParticipant?.publishData(payload, { reliable: true });
                } catch (_) {}
                if (boardViewBroadcastIntervalRef.current) {
                    window.clearInterval(boardViewBroadcastIntervalRef.current);
                }
                boardViewBroadcastIntervalRef.current = window.setInterval(async () => {
                    try {
                        const encoder = new TextEncoder();
                        const pos = { type: 'chess', data: { type: 'position', fen: game.fen(), playerName: participantName, timestamp: Date.now() } } as any;
                        const payload = encoder.encode(JSON.stringify(pos));
                        await room.localParticipant?.publishData(payload, { reliable: false });
                    } catch (_) {}
                }, 2000);
                toast.success('📺 Chequered view shared');
            }
        } catch (e) {
            console.error('Error starting board view share:', e);
            toast.error('Failed to share board');
            setIsBoardShared(false);
            setShareMode('collaboration');
        }
    };

    // Rebroadcast current puzzle (for when initial broadcast failed)
    const rebroadcastBoard = async () => {
        if (!room || !isInMeeting || !isBoardShared) {
            toast.error('Nothing to rebroadcast - share puzzle first');
            return;
        }

        if (userRole !== 'teacher') {
            toast.error('Only teachers can rebroadcast');
            return;
        }

        if (!currentPuzzle) {
            toast.error('No puzzle to rebroadcast');
            return;
        }

        // Prepare rebroadcast data
        const rebroadcastData = {
            type: 'chess',
            action: 'PUZZLE_SHARED',
            fen: currentPuzzle.fen,
            shareMode: 'collaboration',
            playerName: participantName,
            timestamp: Date.now(),
            puzzle: {
                id: currentPuzzle.id,
                fen: currentPuzzle.fen,
                moves: currentPuzzle.moves,
                description: currentPuzzle.description,
                rating: currentPuzzle.rating,
                themes: currentPuzzle.themes,
                complexity: currentPuzzle.complexity
            }
        };

        try {
            const encoder = new TextEncoder();
            const payload = encoder.encode(JSON.stringify(rebroadcastData));
            await room.localParticipant?.publishData(payload, { reliable: true });
            toast.success('Puzzle rebroadcasted to all participants!');
            console.log('📢 Puzzle rebroadcast successful');
        } catch (error) {
            console.error('Error rebroadcasting:', error);
            toast.error('Failed to rebroadcast');
        }
        try {
            const encoder = new TextEncoder();
            const payload = encoder.encode(JSON.stringify(rebroadcastData));
            await room.localParticipant?.publishData(payload, { reliable: true });

            toast.success('Puzzle rebroadcasted to all participants!');
            console.log('📢 Puzzle rebroadcast successful');
        } catch (error) {
            console.error('Error rebroadcasting:', error);
            toast.error('Failed to rebroadcast');
        }
    };

    // Stop sharing board
    const stopSharingBoard = async () => {
        if (!room || !isInMeeting) return;
        
        // For view mode, unpublish and stop the screen-share track
        if (shareMode === 'view' && screenShareTrackRef.current) {
            try {
                room.localParticipant.unpublishTrack(screenShareTrackRef.current);
            } catch {}
            try {
                screenShareTrackRef.current.stop();
            } catch {}
            screenShareTrackRef.current = null;
            if (canvasMirrorIntervalRef.current) {
                window.clearInterval(canvasMirrorIntervalRef.current);
                canvasMirrorIntervalRef.current = null;
            }
            if (boardViewBroadcastIntervalRef.current) {
                window.clearInterval(boardViewBroadcastIntervalRef.current);
                boardViewBroadcastIntervalRef.current = null;
            }
        }

        // Broadcast stop sharing to all students for both modes (so UI closes)
        const stopData = {
            type: 'chess',
            action: shareMode === 'view' ? 'BOARD_VIEW_STOPPED' : 'PUZZLE_STOPPED',
            timestamp: Date.now()
        };
        try {
            const encoder = new TextEncoder();
            const payload = encoder.encode(JSON.stringify(stopData));
            await room.localParticipant?.publishData(payload, { reliable: true });
        } catch {}
        
        // Clear metadata for late joiners (teachers only)
        if (userRole === 'teacher') {
            try {
                await room.localParticipant?.setMetadata('');
            } catch {}
        }
        toast.success('Board sharing stopped');
        
        // Reset local state - restore original mode instead of defaulting to teaching
        setIsBoardShared(false);
        setCurrentPuzzle(null);
        setStudentPositions(new Map());
        setMode(originalMode); // Restore the mode user was in before sharing
        setShareMode('collaboration'); // Reset share mode
        setGame(new Chess()); // Reset board
    };

    // Parse puzzle solution moves and create visual hint arrows (like Lichess)
    const generateHintArrows = useCallback(() => {
        if (!currentPuzzle || !showHint) {
            setHintArrows([]);
            return;
        }

        try {
            const solutionMoves = currentPuzzle.moves.split(' ').filter(m => m.trim());
            const hintArrowsList: Array<[string, string, string]> = [];
            
            // Show only the first move as a hint (like Lichess does)
            if (solutionMoves.length > 0) {
                const firstMove = solutionMoves[0];
                
                // Parse move: could be in format "e2e4" or "Nf3" (SAN notation)
                if (firstMove.length >= 4) {
                    // UCI format: e2e4
                    const from = firstMove.substring(0, 2);
                    const to = firstMove.substring(2, 4);
                    
                    // Add yellow arrow for hint
                    hintArrowsList.push([from, to, 'rgba(255, 193, 7, 0.8)']);
                } else {
                    // Try to parse as SAN notation using chess.js
                    try {
                        const tempGame = new Chess(game.fen());
                        const move = tempGame.move(firstMove);
                        
                        if (move) {
                            hintArrowsList.push([move.from, move.to, 'rgba(255, 193, 7, 0.8)']);
                        }
                    } catch (e) {
                        console.log('Could not parse SAN move for hint:', firstMove);
                    }
                }
            }
            
            setHintArrows(hintArrowsList);
        } catch (error) {
            console.error('Error generating hint arrows:', error);
            setHintArrows([]);
        }
    }, [currentPuzzle, showHint, game]);

    // Update hint arrows when showHint changes
    useEffect(() => {
        generateHintArrows();
    }, [generateHintArrows]);

    // Create custom puzzle from current position
    const createCustomPuzzle = () => {
        if (!newPuzzle.description.trim()) {
            toast.error('Please enter a puzzle description');
            return;
        }
        if (!newPuzzle.solution.trim()) {
            toast.error('Please enter the solution moves (e.g., "e2e4 e7e5" or "Nf3 Nc6")');
            return;
        }

        try {
            // Validate solution moves
            const testGame = new Chess(game.fen());
            const solutionMoves = newPuzzle.solution.trim().split(/\s+/);
            
            for (const move of solutionMoves) {
                try {
                    testGame.move(move);
                } catch {
                    toast.error(`Invalid move in solution: ${move}`);
                    return;
                }
            }

            const customPuzzle: ChessPuzzle = {
                id: `custom-${Date.now()}`,
                fen: game.fen(),
                moves: newPuzzle.solution.trim(),
                rating: newPuzzle.complexity === 'beginner' ? 1200 : newPuzzle.complexity === 'intermediate' ? 1600 : 2000,
                themes: newPuzzle.themes.split(',').map(t => t.trim()).filter(t => t),
                description: newPuzzle.description,
                hint: newPuzzle.hint || undefined,
                complexity: newPuzzle.complexity,
                category: newPuzzle.category,
                source: 'custom'
            };

            // Add to custom puzzles
            const updatedCustomPuzzles = [...customPuzzles, customPuzzle];
            setCustomPuzzles(updatedCustomPuzzles);
            
            // Save to localStorage
            localStorage.setItem('chequeredCustomPuzzles', JSON.stringify(updatedCustomPuzzles));

            // Reset form
            setNewPuzzle({
                description: '',
                hint: '',
                solution: '',
                complexity: 'beginner',
                category: 'tactics',
                themes: ''
            });
            setShowPuzzleCreator(false);

            toast.success('✅ Custom puzzle created successfully!');
            
            // Load the newly created puzzle
            loadPuzzle(customPuzzle);
        } catch (error) {
            toast.error('Failed to create puzzle. Please check your inputs.');
            console.error(error);
        }
    };

    // Delete custom puzzle
    const deleteCustomPuzzle = (puzzleId: string) => {
        const updatedPuzzles = customPuzzles.filter(p => p.id !== puzzleId);
        setCustomPuzzles(updatedPuzzles);
        localStorage.setItem('chequeredCustomPuzzles', JSON.stringify(updatedPuzzles));
        
        if (currentPuzzle?.id === puzzleId) {
            setCurrentPuzzle(null);
            setGame(new Chess());
        }
        
        toast.success('Puzzle deleted');
    };

    // Load custom puzzles from localStorage on mount
    useEffect(() => {
        const savedPuzzles = localStorage.getItem('chequeredCustomPuzzles');
        if (savedPuzzles) {
            try {
                setCustomPuzzles(JSON.parse(savedPuzzles));
            } catch (error) {
                console.error('Failed to load custom puzzles:', error);
            }
        }
    }, []);

    // Switch to teaching mode (free board)
    const switchToTeachingMode = () => {
        // Clean up existing state
        setMode('teaching');
        setOriginalMode('teaching'); // Update original mode when switching
        setShareMode('collaboration'); // Teaching mode uses collaboration
        setIsBoardShared(false); // Reset sharing state
        
        // Reset game state
        const newGame = new Chess(); // Start from initial position
        setGame(newGame);
        setCurrentPuzzle(null);
        setPuzzleProgress([]);
        setIsCorrectMove(null);
        setPuzzleCompleted(false);
        setTeachingModeHistory([]);
        setShowPiecePicker(false);
        setIsPieceSetupMode(false);
        setStudentPositions(new Map());
        
        // Clear any existing metadata
        if (room && room.localParticipant) {
            try {
                room.localParticipant.setMetadata('');
                console.log('🧹 Cleared metadata when switching to teaching mode');
            } catch (error) {
                console.warn('Could not clear metadata:', error);
            }
        }
        setSelectedTrayPiece(null);
        setRightClickedSquares({}); // Clear annotations
        setArrows([]);
        setArrowStart(null);
        setHintArrows([]);
        setShowHint(false);
        toast.info('Teaching mode activated - Board is fully editable! Piece tray and drawing tools available.');
    };

    // Handle piece drop in teaching mode (free play or piece setup)
    const onDropTeaching = (sourceSquare: string, targetSquare: string, piece: string) => {
        // Save current position to history
        const currentFen = game.fen();
        
        try {
            const newGame = new Chess(game.fen());
            
            // Check if this is a pawn promotion move
            const sourcePiece = sourceSquare ? newGame.get(sourceSquare as any) : null;
            const isPromotion = sourcePiece && 
                                sourcePiece.type === 'p' && 
                                ((sourcePiece.color === 'w' && targetSquare[1] === '8') || 
                                 (sourcePiece.color === 'b' && targetSquare[1] === '1'));
            
            // Try as a legal move first
            const move = newGame.move({
            from: sourceSquare,
            to: targetSquare,
                promotion: isPromotion ? 'q' : undefined // Auto-promote to queen, or allow manual selection
            });
            
            if (move) {
                // Legal move succeeded
                setTeachingModeHistory([...teachingModeHistory, currentFen]);
                setGame(newGame);
        setPuzzleProgress([...puzzleProgress, move.san]);

        // Share move in teaching mode too
        if (isInMeeting && room) {
            sendChessData({
                type: 'position',
                from: sourceSquare,
                to: targetSquare,
                san: move.san,
                        fen: newGame.fen(),
                playerName: participantName,
                timestamp: Date.now()
            });
        }

        return true;
            }
            
            // If not a legal move, allow arbitrary placement (piece setup mode)
            // Remove piece from source if it exists
            if (sourceSquare) {
                try {
                    newGame.remove(sourceSquare as any);
                } catch (e) {
                    console.log('No piece at source', e);
                }
            }
            
            // Add piece to target square
            const pieceColor = piece.charAt(0) === 'w' ? 'w' : 'b';
            const pieceType = piece.charAt(1).toLowerCase();
            newGame.put({ type: pieceType as any, color: pieceColor as any }, targetSquare as any);
            
            setTeachingModeHistory([...teachingModeHistory, currentFen]);
            setGame(newGame);
            toast.success(`Piece placed on ${targetSquare}`);
            return true;
        } catch (error) {
            console.error('Error in piece drop:', error);
            toast.error('Could not place piece');
            return false;
        }
    };

    // Undo last move in teaching mode
    const undoTeachingMove = () => {
        if (teachingModeHistory.length === 0) {
            toast.info('Nothing to undo');
            return;
        }

        const previousFen = teachingModeHistory[teachingModeHistory.length - 1];
        const newHistory = teachingModeHistory.slice(0, -1);
        
        setTeachingModeHistory(newHistory);
        setGame(new Chess(previousFen));
        
        // Remove last move from progress
        if (puzzleProgress.length > 0) {
            setPuzzleProgress(puzzleProgress.slice(0, -1));
        }
        
        toast.success('Move undone');
    };

    // Clear all annotations
    const clearAnnotations = () => {
        setRightClickedSquares({});
        setArrowStart(null);
        setArrows([]);
        setHintArrows([]);
        setShowHint(false);
        toast.success('All annotations and arrows cleared');
    };

    // Handle square right-click for highlighting or arrow drawing
    const onSquareRightClick = (square: string) => {
        // Allow annotations in both teaching and puzzle mode
        if (mode !== 'teaching' && mode !== 'puzzle') return;

        if (drawingMode === 'highlight') {
            // Highlight mode
            const color = {
                'green': 'rgba(0, 255, 0, 0.4)',
                'red': 'rgba(255, 0, 0, 0.4)',
                'blue': 'rgba(0, 0, 255, 0.4)',
                'yellow': 'rgba(255, 255, 0, 0.4)'
            }[drawingColor];

            // Toggle highlight on right-click
            const newSquares = { ...rightClickedSquares };
            if (newSquares[square]) {
                delete newSquares[square];
            } else {
                newSquares[square] = { backgroundColor: color };
            }
            setRightClickedSquares(newSquares);
        } else if (drawingMode === 'arrow') {
            // Arrow mode - click first square, then second square
            if (!arrowStart) {
                setArrowStart(square);
                toast.info(`Arrow start: ${square.toUpperCase()}. Right-click another square to complete the arrow.`);
            } else {
                if (arrowStart === square) {
                    // Cancel arrow
                    setArrowStart(null);
                    toast.info('Arrow cancelled');
                } else {
                    // Complete the arrow
                    const arrowColor = {
                        'green': 'rgb(16, 185, 129)',
                        'red': 'rgb(239, 68, 68)',
                        'blue': 'rgb(59, 130, 246)',
                        'yellow': 'rgb(234, 179, 8)'
                    }[drawingColor];
                    
                    setArrows([...arrows, [arrowStart, square, arrowColor]]);
                    setArrowStart(null);
                    toast.success(`Arrow drawn: ${arrowStart.toUpperCase()} → ${square.toUpperCase()}`);
                }
            }
        }
    };

    // Teacher View - Show all student boards in grid (Chesslang style) OR teacher's own puzzle solving (screen share mode)
    console.log('🔍 Debug teacher view:', { userRole, isInMeeting, isBoardShared, studentPositions: studentPositions.size, shareMode, mode });
    // For teaching mode, don't show the monitoring view - just show teaching interface
    if (isTeacher(userRole) && isInMeeting && isBoardShared && !isTeachingMode(mode)) {
        return (
            <div className="space-y-3 h-full flex flex-col">
                {/* Compact Header with Controls */}
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-3 rounded-lg flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-base font-bold flex items-center gap-2">
                                {shareMode === 'collaboration' ? '♟️ Live Monitoring' : '📺 Screen Sharing'} - {currentPuzzle?.description || 'Puzzle'}
                            </h3>
                            <p className="text-xs text-purple-100">
                                {shareMode === 'collaboration' 
                                    ? `${studentPositions.size} students solving • Rating: ${currentPuzzle?.rating || 'N/A'}`
                                    : 'Students are watching you solve the puzzle'
                                }
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                        <button
                                onClick={() => setBoardOrientation(boardOrientation === 'white' ? 'black' : 'white')}
                                className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-medium transition-colors"
                        >
                                🔄 Flip Boards
                        </button>
                            <button
                                onClick={rebroadcastBoard}
                                className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs font-medium transition-colors"
                                title="Rebroadcast to all participants"
                            >
                                📡 Rebroadcast
                        </button>
                            <button
                                onClick={stopSharingBoard}
                                className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-medium transition-colors"
                            >
                                ⏹️ Stop
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content - Student Boards Grid - Chesslang Style - Takes full space */}
                <div className="bg-gray-800 rounded-lg p-3 flex-1 flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-white text-sm flex items-center gap-2">
                                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                                Live Student Progress ({studentPositions.size})
                            </h4>
                            <div className="text-xs text-gray-400">
                                {studentPositions.size} solving
                            </div>
                        </div>
                    
                        {studentPositions.size === 0 ? (
                            <div className="text-center py-16 text-gray-400">
                            <div className="mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto opacity-50">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                            </div>
                                <p className="text-sm">Waiting for students to start solving...</p>
                                <p className="text-xs mt-1 text-gray-500">Their boards will appear here in real-time</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                            {Array.from(studentPositions.values())
                                .sort((a, b) => {
                                    // Completed puzzles go to bottom, active ones on top
                                    if (a.completed && !b.completed) return 1;
                                    if (!a.completed && b.completed) return -1;
                                    return b.timestamp - a.timestamp;
                                })
                                .map((student, idx) => (
                                    <div 
                                        key={student.studentName}
                                        className={`rounded-lg p-2 border-2 transition-all ${
                                            student.completed 
                                                ? 'bg-green-900/50 border-green-500 shadow-lg shadow-green-500/50' 
                                                : 'bg-gray-700 border-gray-600 hover:border-emerald-500'
                                        }`}
                                    >
                                        {/* Student Info Header */}
                                        <div className="flex items-center justify-between mb-1.5">
                                            <div className="flex items-center gap-1 flex-1 min-w-0">
                                                <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                                    student.completed 
                                                        ? 'bg-green-500 text-white' 
                                                        : 'bg-emerald-600 text-white'
                                                }`}>
                                                    {student.completed ? '✓' : idx + 1}
                                                </span>
                                                <span className="font-semibold text-white text-xs truncate">
                                                    {student.studentName}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Mini Chess Board */}
                                        <div className="relative mb-1.5">
                                            {/* Zoom Controls - Outside the transform container */}
                                            <div className="chess-zoom-controls" style={{ position: 'absolute', right: '12px', top: '-40px', zIndex: 50 }}>
                                                <button
                                                    onClick={() => {
                                                        const currentZoom = studentZoomLevels[student.studentName] || 100;
                                                        const newZoom = Math.max(50, currentZoom - 10);
                                                        setStudentZoomLevels(prev => ({
                                                            ...prev,
                                                            [student.studentName]: newZoom
                                                        }));
                                                        console.log('🔍 Zoom out:', student.studentName, newZoom);
                                                    }}
                                                    title="Zoom Out"
                                                    className="bg-gray-800 hover:bg-gray-700 text-white"
                                                >
                                                    −
                                                </button>
                                                <div className="zoom-level bg-gray-800 text-white">{studentZoomLevels[student.studentName] || 100}%</div>
                                                <button
                                                    onClick={() => {
                                                        const currentZoom = studentZoomLevels[student.studentName] || 100;
                                                        const newZoom = Math.min(150, currentZoom + 10);
                                                        setStudentZoomLevels(prev => ({
                                                            ...prev,
                                                            [student.studentName]: newZoom
                                                        }));
                                                        console.log('🔍 Zoom in:', student.studentName, newZoom);
                                                    }}
                                                    title="Zoom In"
                                                    className="bg-gray-800 hover:bg-gray-700 text-white"
                                                >
                                                    +
                                                </button>
                                            </div>
                                            
                                            {/* Board Container - With transform */}
                                            <div 
                                                className={`chess-board-container ${student.completed ? 'opacity-75' : ''}`}
                                                style={{
                                                    transform: `scale(${(studentZoomLevels[student.studentName] || 100) / 100})`,
                                                    transformOrigin: 'top center',
                                                    maxWidth: '100%'
                                                }}
                                            >
                                            <Chessboard
                                                position={student.fen}
                                                boardOrientation={boardOrientation}
                                                customBoardStyle={{
                                                    borderRadius: '4px'
                                                }}
                                                arePiecesDraggable={false}
                                            />
                                        </div>
                                        </div>

                                        {/* Status with Multi-Step Progress */}
                                        <div className={`text-center rounded px-2 py-1 ${
                                            student.completed 
                                                ? 'bg-green-600' 
                                                : 'bg-gray-600'
                                        }`}>
                                            <span className="text-[10px] font-mono text-white">
                                                {student.completed 
                                                    ? `✓ ${student.totalMoves || 0} moves` 
                                                    : student.lastMove
                                                }
                                            </span>
                                            
                                            {/* Multi-Step Progress Dots */}
                                            {currentPuzzle && !student.completed && (
                                                <div className="flex justify-center gap-0.5 mt-1">
                                                    {Array.from({ length: currentPuzzle.moves.split(' ').length }, (_, i) => {
                                                        const completedMoves = student.totalMoves || 0;
                                                        const totalMovesLocal = currentPuzzle.moves.split(' ').length;
                                                        if (totalMovesLocal <= 1) return null;
                                                        return (
                                                            <div
                                                                key={i}
                                                                className={`w-1 h-1 rounded-full ${
                                                                    i < completedMoves
                                                                        ? 'bg-green-400'
                                                                        : i === completedMoves
                                                                        ? 'bg-yellow-400'
                                                                        : 'bg-gray-400'
                                                                }`}
                                                                title={`Move ${i + 1}${i < completedMoves ? ' ✓' : i === completedMoves ? ' (current)' : ''}`}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            }
                            </div>
                        )}
                </div>
            </div>
        );
    }

    // Student View - Minimal UI for solving / viewing
    if (isStudent(userRole) && isInMeeting) {
        // If teacher is sharing board view, show the board even without a puzzle
        if (shareMode === 'view' && isBoardShared) {
            return (
                <div className="h-full flex items-center justify-center p-4">
                    <div className="w-full" style={{ maxWidth: boardWidthPx, width: boardWidthPx }}>
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 rounded-t-lg">
                            <h3 className="font-bold text-sm mb-1">Teacher Demonstration</h3>
                            <p className="text-xs text-blue-100">Watching teacher's moves...</p>
                        </div>
                        <div className="bg-white rounded-b-lg shadow-lg p-4">
                            <div className="w-full aspect-square">
                                <Chessboard
                                    position={game.fen()}
                                    onPieceDrop={() => false}
                                    arePiecesDraggable={false}
                                    boardOrientation={boardOrientation}
                                    boardWidth={effectiveBoardWidth}
                                    customDarkSquareStyle={{ backgroundColor: '#b58863' }}
                                    customLightSquareStyle={{ backgroundColor: '#f0d9b5' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Waiting for puzzle
        if (!currentPuzzle) {
            return (
                <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg">
                    <div className="text-center px-6">
                        <div className="mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-purple-400 animate-pulse">
                                <path d="M12 2l2 4h4l-3 3 1 4-4-2-4 2 1-4-3-3h4z"></path>
                                <rect x="2" y="14" width="20" height="8" rx="1"></rect>
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">
                            Waiting for Teacher
                        </h3>
                        <p className="text-gray-400 text-sm">
                            The teacher will share a puzzle soon. Get ready to solve it!
                        </p>
                        <div className="mt-4 flex items-center justify-center gap-2">
                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-ping"></span>
                            <span className="text-xs text-gray-500">Connected</span>
                        </div>
                    </div>
                </div>
            );
        }

        // Student has puzzle - minimal solving UI
        return (
            <div className="h-full flex items-center justify-center p-4">
                <div className="w-full" style={{ maxWidth: boardWidthPx, width: boardWidthPx }}>
                    {/* Puzzle Info */}
                    <div className={`bg-gradient-to-r ${isTeachingMode(mode) ? 'from-blue-600 to-indigo-600' : 'from-green-600 to-emerald-600'} text-white p-3 rounded-t-lg`}>
                        <h3 className="font-bold text-sm mb-1">
                            {currentPuzzle.description || 'Solve the Puzzle'}
                        </h3>
                        {currentPuzzle.hint && isPuzzleMode(mode) && (
                            <p className="text-xs opacity-90 mb-1">💡 {currentPuzzle.hint}</p>
                        )}
                        <div className="flex items-center gap-3 text-xs">
                            <span>Rating: {currentPuzzle.rating}</span>
                            <span>•</span>
                            <span>{currentPuzzle.complexity || 'intermediate'}</span>
                            {mode === 'puzzle' && (
                                <>
                                    <span>•</span>
                                    <span className="bg-white/20 px-2 py-0.5 rounded">🤝 Collaboration Mode</span>
                                </>
                            )}
                            {puzzleCompleted && (
                                <>
                                    <span>•</span>
                                    <span className="text-green-300 font-semibold">✓ Solved!</span>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Chess Board */}
                    <div className="bg-white p-4 rounded-b-lg shadow-lg">
                        <div className="relative">
                            {/* Zoom Controls - Teachers only */}
                            {isTeacher(userRole) && (
                            <div className="chess-zoom-controls" style={{ position: 'absolute', right: '12px', top: '-40px', zIndex: 50 }}>
                                <button
                                    onClick={() => {
                                        const newZoom = Math.max(50, mainZoomLevel - 10);
                                        setMainZoomLevel(newZoom);
                                        console.log('🔍 Main board zoom out:', newZoom);
                                    }}
                                    title="Zoom Out"
                                    className="bg-gray-800 hover:bg-gray-700 text-white"
                                >
                                    −
                                </button>
                                <div className="zoom-level bg-gray-800 text-white">{mainZoomLevel}%</div>
                                <button
                                    onClick={() => {
                                        const newZoom = Math.min(150, mainZoomLevel + 10);
                                        setMainZoomLevel(newZoom);
                                        console.log('🔍 Main board zoom in:', newZoom);
                                    }}
                                    title="Zoom In"
                                    className="bg-gray-800 hover:bg-gray-700 text-white"
                                >
                                    +
                                </button>
                                <button
                                    onClick={() => {
                                        setMainZoomLevel(100);
                                        console.log('🔍 Main board zoom reset');
                                    }}
                                    title="Reset Zoom"
                                    className="bg-gray-800 hover:bg-gray-700 text-white text-xs px-2"
                                >
                                    Reset
                                </button>
                            </div>
                            )}
                            
                            {/* Board Container - With transform */}
                            <div 
                                ref={boardContainerRef}
                                className={`chess-board-container ${isTeachingMode(mode) ? 'relative' : ''}`}
                                style={{
                                    transform: isTeacher(userRole) ? `scale(${mainZoomLevel / 100})` : 'none',
                                    maxWidth: '100%',
                                    transformOrigin: 'top center'
                                }}
                            >
                            <Chessboard
                                position={game.fen()}
                                onPieceDrop={onDrop}
                                arePiecesDraggable={!(userRole === 'student' && shareMode === 'view')}
                                boardOrientation={boardOrientation}
                                boardWidth={effectiveBoardWidth}
                                customBoardStyle={{
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    opacity: 1
                                }}
                                customDarkSquareStyle={{ backgroundColor: '#b58863' }}
                                customLightSquareStyle={{ backgroundColor: '#f0d9b5' }}
                            />
                            {(userRole === 'student' && shareMode === 'view') && (
                                <style>{`
                                    .chess-board-container img, 
                                    .chess-board-container svg {
                                        -webkit-user-drag: none;
                                        user-select: none;
                                    }
                                `}</style>
                            )}
                        </div>
                        </div>

                        {/* Simple Controls - Different for each mode */}
                        <div className="mt-4 flex items-center justify-center gap-3">
                            <button
                                onClick={() => setBoardOrientation(boardOrientation === 'white' ? 'black' : 'white')}
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
                            >
                                🔄 Flip Board
                            </button>
                            {mode === 'puzzle' && (
                                <button
                                    onClick={resetPuzzle}
                                    className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors"
                                >
                                    🔁 Reset
                                </button>
                            )}
                        </div>

                        {/* Multi-Step Progress Indicator */}
                        {currentPuzzle && (
                            <div className="mt-3 text-center">
                                {(() => {
                                    const totalMoves = currentPuzzle.moves.split(' ').length;
                                    const completedMoves = puzzleProgress.length;
                                    
                                    if (totalMoves > 1) {
                                        return (
                                            <div className="space-y-2">
                                                {/* Progress Bar */}
                                                <div className="flex items-center justify-center gap-2">
                                                    <span className="text-xs text-gray-500">
                                                        {isTeachingMode(mode) ? "Teacher's Progress:" : 'Your Progress:'}
                                                    </span>
                                                    <div className="flex items-center gap-1">
                                                        {Array.from({ length: totalMoves }, (_, i) => (
                                                            <div
                                                                key={i}
                                                                className={`w-3 h-3 rounded-full border-2 ${
                                                                    i < completedMoves
                                                                        ? 'bg-green-500 border-green-500'
                                                                        : i === completedMoves
                                                                        ? 'bg-yellow-400 border-yellow-400 animate-pulse'
                                                                        : 'bg-gray-200 border-gray-300'
                                                                }`}
                                                                title={`Move ${i + 1}${i < completedMoves ? ' ✓' : i === completedMoves ? ' (current)' : ''}`}
                                                            />
                                                        ))}
                                                    </div>
                                                    <span className="text-xs text-gray-600">
                                                        {completedMoves}/{totalMoves}
                                                    </span>
                                                </div>
                                                
                                                {/* Move List */}
                                                {puzzleProgress.length > 0 && (
                                                    <p className="text-xs text-gray-500">
                                                        {isTeachingMode(mode) ? "Teacher's Moves:" : 'Completed:'} <span className="font-mono font-semibold text-green-600">{puzzleProgress.join(' → ')}</span>
                                                    </p>
                                                )}
                                                
                                                {/* Current Move Hint - Only show in puzzle mode */}
                                                {completedMoves < totalMoves && isPuzzleMode(mode) && (
                                                    <p className="text-xs text-blue-600 font-medium">
                                                        Next: Find move #{completedMoves + 1}
                                                    </p>
                                                )}
                                                
                                                {/* Watching indicator - Only in teaching mode */}
                                                {completedMoves < totalMoves && mode === TEACHING_MODE && (
                                                    <p className="text-xs text-blue-600 font-medium flex items-center justify-center gap-1">
                                                        <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                                                        Watching teacher's moves...
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    } else {
                                        return (
                                            <p className="text-xs text-gray-500">
                                                Single move puzzle
                                            </p>
                                        );
                                    }
                                })()}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Regular view (Student with puzzle or Teacher when not sharing)
    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-lg">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-bold flex items-center gap-2">
                            ♟️ Chequered - Interactive Chess
                        </h3>
                        <p className="text-sm text-purple-100">
                            {userRole === 'student' ? 'Solve the puzzle shared by your teacher' : 'Solve puzzles, analyze positions, and collaborate in live classes'}
                        </p>
                    </div>
                    {userRole === 'teacher' && (
                        <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-medium">
                            Teacher Mode
                        </span>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                {/* Chess Board - Takes 3/4 of width */}
                <div className="lg:col-span-3">
                    <div className="bg-white rounded-lg shadow-lg p-3">
                        {/* Compact Board Controls - Puzzle Mode */}
                        {mode === PUZZLE_MODE && (
                            <div className="mb-3 flex items-center justify-between gap-2">
                                {/* Left side - Essential puzzle controls */}
                                <div className="flex items-center gap-1.5">
                                    {currentPuzzle && (
                                    <>
                                        <button
                                            onClick={resetPuzzle}
                                                className="px-2.5 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded text-xs font-medium transition-colors flex items-center gap-1"
                                                title="Reset puzzle to start"
                                        >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Reset
                                        </button>
                                        <button
                                            onClick={() => setShowHint(!showHint)}
                                                className={`px-2.5 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1 ${
                                                showHint
                                                        ? 'bg-yellow-500 text-white shadow-sm'
                                                    : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                            }`}
                                                title="Show/hide hint arrow"
                                        >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                </svg>
                                                Hint
                                        </button>
                                    </>
                                )}
                                            <button
                                        onClick={() => setBoardOrientation(boardOrientation === 'white' ? 'black' : 'white')}
                                        className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs font-medium transition-colors flex items-center gap-1"
                                        title="Flip board"
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Flip
                                            </button>
                                    {teachingModeHistory.length > 0 && (
                                        <button
                                            onClick={undoTeachingMove}
                                            disabled={teachingModeHistory.length === 0}
                                            className="px-2.5 py-1.5 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:text-gray-500 text-white rounded text-xs font-medium transition-colors flex items-center gap-1"
                                            title="Undo last move"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                            </svg>
                                            Undo
                                        </button>
                                    )}
                                    {Object.keys(rightClickedSquares).length > 0 && (
                                        <button
                                            onClick={clearAnnotations}
                                            className="px-2.5 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded text-xs font-medium transition-colors relative flex items-center gap-1"
                                            title="Clear all annotations"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            Clear
                                            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold px-1 rounded-full">
                                                    {Object.keys(rightClickedSquares).length}
                                    </span>
                                        </button>
                                    )}
                                </div>

                                {/* Right side - Sharing actions (teacher only) */}
                                {isInMeeting && userRole === 'teacher' && (
                                    <div className="flex items-center gap-1.5">
                                        {!isBoardShared && (
                                        <button
                                                onClick={shareBoardWithStudents}
                                                className="px-2.5 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs font-medium transition-colors flex items-center gap-1"
                                                title="Share puzzle with students"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                                </svg>
                                                Share
                                        </button>
                                        )}
                                        <button
                                            onClick={() => window.open(`http://localhost:5173/chequered/${subscriptionId}`, '_blank', 'noopener')}
                                            className="px-2.5 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs font-medium transition-colors flex items-center gap-1"
                                            title="Open in new tab"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                            New Tab
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Teaching Mode Board Controls - already organized in sidebar */}
                        {mode === TEACHING_MODE && (
                            <div className="mb-3 flex items-center gap-2">
                                <span className="text-xs text-gray-600 font-medium">
                                    📚 Teaching Mode - Use sidebar controls for board actions
                                </span>
                            </div>
                        )}

                        {/* Compact Meeting Status - Only show if sharing or in meeting */}
                        {isInMeeting && (
                            <div className="mb-2 flex items-center justify-end gap-2">
                                {userRole === 'teacher' && isBoardShared && (
                                    <button
                                        onClick={stopSharingBoard}
                                        className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium transition-colors flex items-center gap-1"
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                        Stop Sharing
                                    </button>
                                )}
                                {userRole === 'teacher' && mode === TEACHING_MODE && !isBoardShared && (
                                    <button
                                        onClick={shareBoardViewMode}
                                        className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors flex items-center gap-1"
                                        title="Share board in view mode"
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        Share Board
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Student - Meeting Indicator */}
                        {isInMeeting && userRole !== 'teacher' && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                    Live - Moves shared with teacher
                                </span>
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            {isCorrectMove !== null && !puzzleCompleted && (
                                <div className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                                    isCorrectMove 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-red-100 text-red-700'
                                }`}>
                                    {isCorrectMove ? '✅ Correct!' : '❌ Try again'}
                                </div>
                            )}

                            {puzzleCompleted && (
                                <div className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm font-medium">
                                    🎉 Puzzle Solved!
                                </div>
                            )}
                        </div>

                        {/* Visual Hint Indicator */}
                        {showHint && currentPuzzle && mode === PUZZLE_MODE && (
                            <div className="mb-3 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xl">💡</span>
                                        <div>
                                            <p className="text-sm font-semibold text-yellow-900">Visual Hint Active</p>
                                            <p className="text-xs text-yellow-700">Yellow arrow shows the best move</p>
                                        </div>
                                    </div>
                                    <div className="text-2xl animate-pulse">→</div>
                                </div>
                                {currentPuzzle.hint && (
                                    <p className="text-xs text-yellow-800 mt-2 italic">"{currentPuzzle.hint}"</p>
                                )}
                            </div>
                        )}

                        {/* Chessboard - Full Width */}
                        <div className="flex justify-center items-start">
                            {/* Hidden Piece Tray - Moved to Sidebar */}
                            {false && mode === 'teaching' && (
                                <div className="flex-shrink-0 w-48 flex flex-col gap-3 p-4 bg-gradient-to-b from-blue-100 to-purple-100 rounded-lg border-4 border-blue-500 shadow-xl">
                                    <div className="text-center">
                                        <div className="text-sm font-bold text-blue-900 mb-1 bg-white rounded py-2 px-3 shadow">
                                            🎨 Piece Tray
                                        </div>
                                        <p className="text-[10px] text-blue-800 mt-1 bg-blue-50 rounded px-2 py-1">
                                            Click piece → Click board square
                                        </p>
                                        {selectedTrayPiece ? (
                                            <div className="space-y-1">
                                                <div className="bg-green-500 text-white text-[10px] font-bold px-2 py-1 rounded animate-pulse">
                                                    Selected: {selectedTrayPiece.color === 'w' ? '⚪' : '⚫'} {
                                                        {
                                                            'K': 'King', 'Q': 'Queen', 'R': 'Rook',
                                                            'B': 'Bishop', 'N': 'Knight', 'P': 'Pawn'
                                                        }[selectedTrayPiece.piece]
                                                    }
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setSelectedTrayPiece(null);
                                                        toast.info('Piece deselected');
                                                    }}
                                                    className="w-full text-[10px] px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded font-semibold"
                                                >
                                                    ✖ Deselect
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="bg-gray-200 text-gray-600 text-[10px] px-2 py-1 rounded">
                                                No piece selected
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* White Pieces */}
                                    <div className="bg-white rounded-lg p-3 shadow-md border-2 border-gray-200">
                                        <div className="text-xs font-bold text-gray-700 mb-2 text-center pb-2 border-b border-gray-300">⚪ White Pieces</div>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { piece: 'K', symbol: '♔', name: 'King', count: '×1' },
                                                { piece: 'Q', symbol: '♕', name: 'Queen', count: '×1' },
                                                { piece: 'R', symbol: '♖', name: 'Rook', count: '×2' },
                                                { piece: 'B', symbol: '♗', name: 'Bishop', count: '×2' },
                                                { piece: 'N', symbol: '♘', name: 'Knight', count: '×2' },
                                                { piece: 'P', symbol: '♙', name: 'Pawn', count: '×8' }
                                            ].map(({ piece, symbol, name, count }) => (
                                                <div
                                                    key={`spare-w-${piece}`}
                                                    onClick={() => {
                                                        setSelectedTrayPiece({ piece, color: 'w' });
                                                        toast.success(`Selected White ${name}! Click on any board square to place it.`);
                                                    }}
                                                    className="relative group"
                                                >
                                                    <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-br from-white to-gray-50 border-2 rounded-lg cursor-pointer hover:shadow-lg hover:scale-110 transition-all text-3xl ${
                                                        selectedTrayPiece?.piece === piece && selectedTrayPiece?.color === 'w'
                                                            ? 'border-green-500 ring-2 ring-green-400 scale-110'
                                                            : 'border-gray-300 hover:border-blue-500'
                                                    }`}>
                                                        {symbol}
                                                    </div>
                                                    <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-[8px] font-bold px-1 rounded-full">
                                                        {count}
                                                    </div>
                                                    <div className="text-[8px] text-center text-gray-600 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        {name}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Black Pieces */}
                                    <div className="bg-gray-800 rounded-lg p-3 shadow-md border-2 border-gray-600">
                                        <div className="text-xs font-bold text-white mb-2 text-center pb-2 border-b border-gray-600">⚫ Black Pieces</div>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { piece: 'K', symbol: '♚', name: 'King', count: '×1' },
                                                { piece: 'Q', symbol: '♛', name: 'Queen', count: '×1' },
                                                { piece: 'R', symbol: '♜', name: 'Rook', count: '×2' },
                                                { piece: 'B', symbol: '♝', name: 'Bishop', count: '×2' },
                                                { piece: 'N', symbol: '♞', name: 'Knight', count: '×2' },
                                                { piece: 'P', symbol: '♟', name: 'Pawn', count: '×8' }
                                            ].map(({ piece, symbol, name, count }) => (
                                                <div
                                                    key={`spare-b-${piece}`}
                                                    onClick={() => {
                                                        setSelectedTrayPiece({ piece, color: 'b' });
                                                        toast.success(`Selected Black ${name}! Click on any board square to place it.`);
                                                    }}
                                                    className="relative group"
                                                >
                                                    <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-br from-white to-gray-50 border-2 rounded-lg cursor-pointer hover:shadow-lg hover:scale-110 transition-all text-3xl ${
                                                        selectedTrayPiece?.piece === piece && selectedTrayPiece?.color === 'b'
                                                            ? 'border-yellow-400 ring-2 ring-yellow-300 scale-110'
                                                            : 'border-gray-400 hover:border-yellow-400'
                                                    }`}>
                                                        {symbol}
                                                    </div>
                                                    <div className="absolute -top-1 -right-1 bg-yellow-500 text-gray-900 text-[8px] font-bold px-1 rounded-full">
                                                        {count}
                                                    </div>
                                                    <div className="text-[8px] text-center text-gray-300 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        {name}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="text-center mt-2 space-y-1">
                                        <div className="p-2 bg-green-50 rounded border border-green-300">
                                            <p className="text-[10px] text-green-800 font-semibold">
                                                ✨ Unlimited pieces!<br/>
                                                Click any piece multiple times
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                try {
                                                    const newGame = new Chess(game.fen());
                                                    // Remove both kings for truly empty board
                                                    newGame.remove('e1' as any);
                                                    newGame.remove('e8' as any);
                                                    setGame(newGame);
                                                    toast.success('Kings removed - board is now empty!');
                                                } catch (error) {
                                                    console.log('Could not remove kings, using different method');
                                                    // If that fails, just acknowledge it
                                                    toast.info('Tip: Click on kings to remove them');
                                                }
                                            }}
                                            className="w-full text-[10px] px-2 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded font-semibold"
                                        >
                                            🗑️ Remove Kings (Empty Board)
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            {/* Chess Board */}
                            <div 
                                className="w-full max-w-4xl"
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'move';
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const pieceData = e.dataTransfer.getData('piece');
                                    if (pieceData && mode === TEACHING_MODE) {
                                        console.log('Dropped piece:', pieceData, 'on board');
                                        toast.info('Click on a square to place the piece');
                                    }
                                }}
                            >
                                <div className="w-full aspect-square">
                                    <Chessboard
                                        position={game.fen()}
                                        onPieceDrop={mode === TEACHING_MODE ? onDropTeaching : onDrop}
                                        customArrows={[...arrows, ...hintArrows] as any}
                                        customSquareStyles={(() => {
                                            // Merge existing annotations with selected squares
                                            const styles: any = { ...rightClickedSquares };
                                            // Ensure selected squares also show their highlights
                                            selectedSquares.forEach((square) => {
                                                if (!styles[square]) {
                                                    styles[square] = { backgroundColor: drawingColor === 'green' ? 'rgba(34, 197, 94, 0.4)' : 
                                                                       drawingColor === 'red' ? 'rgba(239, 68, 68, 0.4)' :
                                                                       drawingColor === 'blue' ? 'rgba(59, 130, 246, 0.4)' :
                                                                       'rgba(234, 179, 8, 0.4)' };
                                                }
                                            });
                                            return styles;
                                        })()}
                                        onSquareClick={(square) => {
                                            // Handle square clicks for piece placement/removal/selection in teaching mode
                                            if (mode === TEACHING_MODE) {
                                                const currentFen = game.fen();
                                                const newGame = new Chess(game.fen());
                                                const existingPiece = newGame.get(square as any);
                                                
                                                if (selectedTrayPiece) {
                                                    // Placing a piece from tray
                                                    console.log('Placing piece:', selectedTrayPiece, 'on square:', square);
                                                    try {
                                                        setTeachingModeHistory([...teachingModeHistory, currentFen]);
                                                        // Remove any existing piece on the square
                                                        if (existingPiece) {
                                                            console.log('Removing existing piece:', existingPiece);
                                                            newGame.remove(square as any);
                                                        }
                                                        
                                                        // Place the selected piece
                                                        const pieceType = selectedTrayPiece.piece.toLowerCase();
                                                        const result = newGame.put(
                                                            { 
                                                                type: pieceType as any, 
                                                                color: selectedTrayPiece.color as any 
                                                            }, 
                                                            square as any
                                                        );
                                                        
                                                        console.log('Put result:', result, 'New FEN:', newGame.fen());
                                                        setGame(newGame);
                                                        
                                                        const pieceName = {
                                                            'k': 'King', 'q': 'Queen', 'r': 'Rook',
                                                            'b': 'Bishop', 'n': 'Knight', 'p': 'Pawn'
                                                        }[pieceType] || pieceType;
                                                        
                                                        toast.success(`✅ Placed ${selectedTrayPiece.color === 'w' ? 'White' : 'Black'} ${pieceName} on ${square.toUpperCase()}`);
                                                        
                                                        // Auto-deselect after placing piece
                                                        setSelectedTrayPiece(null);
                                                    } catch (error: any) {
                                                        console.error('Error placing piece:', error, error.message);
                                                        toast.error(`Error: ${error.message || 'Could not place piece'}`);
                                                    }
                                                } else if (interactionMode === 'select' && existingPiece) {
                                                    // Select mode - highlight selected pieces
                                                    const newSelectedSquares = new Set(selectedSquares);
                                                    if (newSelectedSquares.has(square)) {
                                                        newSelectedSquares.delete(square);
                                                        setSelectedSquares(newSelectedSquares);
                                                        // Remove highlight
                                                        const newHighlights = { ...rightClickedSquares };
                                                        delete newHighlights[square];
                                                        setRightClickedSquares(newHighlights);
                                                        toast.info('Selection cleared');
                                                    } else {
                                                        newSelectedSquares.add(square);
                                                        setSelectedSquares(newSelectedSquares);
                                                        // Add highlight with current drawing color
                                                        setRightClickedSquares({
                                                            ...rightClickedSquares,
                                                            [square]: { backgroundColor: drawingColor === 'green' ? 'rgba(34, 197, 94, 0.4)' : 
                                                                       drawingColor === 'red' ? 'rgba(239, 68, 68, 0.4)' :
                                                                       drawingColor === 'blue' ? 'rgba(59, 130, 246, 0.4)' :
                                                                       'rgba(234, 179, 8, 0.4)' }
                                                        });
                                                        const pieceName = {
                                                            'k': 'King', 'q': 'Queen', 'r': 'Rook',
                                                            'b': 'Bishop', 'n': 'Knight', 'p': 'Pawn'
                                                        }[existingPiece.type] || existingPiece.type;
                                                        toast.success(`✓ Selected ${existingPiece.color === 'w' ? 'White' : 'Black'} ${pieceName} on ${square.toUpperCase()}`);
                                                    }
                                                } else if (interactionMode === 'delete' && existingPiece) {
                                                    // Delete mode - remove piece
                                                    try {
                                                        setTeachingModeHistory([...teachingModeHistory, currentFen]);
                                                        newGame.remove(square as any);
                                                        setGame(newGame);
                                                        // Also remove from selected squares if it was selected
                                                        const newSelectedSquares = new Set(selectedSquares);
                                                        newSelectedSquares.delete(square);
                                                        setSelectedSquares(newSelectedSquares);
                                                        const newHighlights = { ...rightClickedSquares };
                                                        delete newHighlights[square];
                                                        setRightClickedSquares(newHighlights);
                                                        const pieceName = {
                                                            'k': 'King', 'q': 'Queen', 'r': 'Rook',
                                                            'b': 'Bishop', 'n': 'Knight', 'p': 'Pawn'
                                                        }[existingPiece.type] || existingPiece.type;
                                                        toast.info(`🗑️ Removed ${existingPiece.color === 'w' ? 'White' : 'Black'} ${pieceName} from ${square.toUpperCase()}`);
                                                    } catch (error: any) {
                                                        console.error('Error removing piece:', error);
                                                        toast.error('Could not remove piece');
                                                    }
                                                } else if (interactionMode === 'move') {
                                                    // Move mode - do nothing on click, dragging handles moves
                                                    // This is handled by onPieceDrop
                                                }
                                            }
                                        }}
                                        onSquareRightClick={onSquareRightClick}
                                boardOrientation={boardOrientation}
                                customBoardStyle={{
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                }}
                                customDarkSquareStyle={{ backgroundColor: mode === TEACHING_MODE ? '#769656' : '#b58863' }}
                                customLightSquareStyle={{ backgroundColor: mode === TEACHING_MODE ? '#eeeed2' : '#f0d9b5' }}
                                arePiecesDraggable={true}
                                customDropSquareStyle={{
                                    boxShadow: 'inset 0 0 1px 6px rgba(59, 130, 246, 0.5)'
                                }}
                            />
                                </div>
                            </div>
                            
                            {/* Annotations Panel - Moved to Sidebar */}
                            {false && mode === 'teaching' && (
                                <div className="flex-shrink-0 w-56 bg-white rounded-lg shadow-lg p-3 border-2 border-purple-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-semibold text-gray-800 text-sm">✏️ Annotations</h4>
                                        <div className="flex gap-1 text-xs">
                                            {Object.keys(rightClickedSquares).length > 0 && (
                                                <span className="bg-purple-500 text-white font-bold px-1.5 py-0.5 rounded-full">
                                                    {Object.keys(rightClickedSquares).length}
                                                </span>
                                            )}
                                            {arrows.length > 0 && (
                                                <span className="bg-blue-500 text-white font-bold px-1.5 py-0.5 rounded-full">
                                                    {arrows.length}↗
                                                </span>
                                            )}
                            </div>
                                    </div>
                                    
                                    {/* Drawing Mode */}
                                    <div className="mb-3">
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                onClick={() => {
                                                    setDrawingMode('highlight');
                                                    setArrowStart(null);
                                                    setSelectedTrayPiece(null);
                                                }}
                                                className={`px-2 py-2 rounded text-xs font-medium transition-all ${
                                                    drawingMode === 'highlight' && !selectedTrayPiece
                                                        ? 'bg-purple-600 text-white shadow-lg'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                            >
                                                ⬛ Highlight
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setDrawingMode('arrow');
                                                    setArrowStart(null);
                                                    setSelectedTrayPiece(null);
                                                }}
                                                className={`px-2 py-2 rounded text-xs font-medium transition-all ${
                                                    drawingMode === 'arrow' && !selectedTrayPiece
                                                        ? 'bg-purple-600 text-white shadow-lg'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                            >
                                                ➡️ Arrow
                                            </button>
                                        </div>
                                        {arrowStart && (
                                            <div className="mt-2 p-2 bg-blue-50 border border-blue-300 rounded text-xs text-blue-900 text-center">
                                                Start: <strong>{arrowStart.toUpperCase()}</strong>
                                                <br />
                                                <span className="text-[10px]">Right-click end square</span>
                                            </div>
                                        )}
                                        {selectedTrayPiece && (
                                            <div className="mt-2 p-2 bg-green-50 border border-green-300 rounded text-xs text-green-900 text-center">
                                                <strong>Piece Mode Active</strong>
                                                <br />
                                                <span className="text-[10px]">Click board to place</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Color Palette */}
                                    <div className="mb-3">
                                        <p className="text-xs font-semibold text-gray-600 mb-2">Color:</p>
                                        <div className="grid grid-cols-4 gap-1">
                                            {[
                                                { color: 'green' as const, bg: 'bg-green-500' },
                                                { color: 'red' as const, bg: 'bg-red-500' },
                                                { color: 'blue' as const, bg: 'bg-blue-500' },
                                                { color: 'yellow' as const, bg: 'bg-yellow-400' }
                                            ].map(({ color, bg }) => (
                                                <button
                                                    key={color}
                                                    onClick={() => setDrawingColor(color)}
                                                    className={`h-9 rounded border-2 transition-all ${bg} ${
                                                        drawingColor === color 
                                                            ? 'border-gray-900 ring-2 ring-gray-400' 
                                                            : 'border-gray-300'
                                                    }`}
                                                >
                                                    {drawingColor === color && <span className="text-white font-bold">✓</span>}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Quick Actions */}
                                    <div className="space-y-2">
                                        <button
                                            onClick={() => {
                                                setSelectedTrayPiece(null);
                                                setArrowStart(null);
                                            }}
                                            className="w-full text-xs px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded font-medium transition-colors"
                                        >
                                            ✖️ Cancel Selection
                                        </button>
                                        <button
                                            onClick={clearAnnotations}
                                            className="w-full text-xs px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded font-medium transition-colors"
                                        >
                                            🗑️ Clear Annotations
                                        </button>
                                    </div>

                                    {/* Quick Guide */}
                                    <div className="mt-3 text-xs text-gray-600 bg-gray-50 rounded p-2">
                                        <p className="font-semibold mb-1">Quick Guide:</p>
                                        <p className="text-xs leading-relaxed">
                                            Right-click squares to {drawingMode === 'highlight' ? 'highlight them' : 'draw arrows'}. 
                                            {drawingMode === 'arrow' && ' Click start, then end square.'}
                                        </p>
                                        <p className="mt-1 text-xs">
                                            Press <kbd className="px-1 py-0.5 bg-gray-200 rounded">ESC</kbd> to cancel
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                        

                        {/* Teaching Mode Indicator */}
                        {mode === TEACHING_MODE && !isPieceSetupMode && (
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                                <p className="text-sm text-green-800">
                                    <strong>📚 Teaching Mode:</strong> All pieces are moveable. Demonstrate concepts freely! Use "Clear Board" to set up custom positions.
                                </p>
                            </div>
                        )}

                        {/* Piece Setup Mode Indicator */}
                        {mode === TEACHING_MODE && isPieceSetupMode && (
                            <div className="mt-3 p-4 bg-blue-50 border-2 border-blue-400 rounded-lg">
                                <div className="text-center mb-2">
                                    <p className="text-sm font-bold text-blue-900">
                                        🎨 Piece Setup Mode Active
                                    </p>
                                </div>
                                <div className="text-xs text-blue-800 space-y-1">
                                    <p className="font-semibold">How to use:</p>
                                    <ul className="list-disc list-inside space-y-1 ml-2">
                                        <li>Drag pieces from the left tray onto the board</li>
                                        <li>Drag pieces on the board to move them</li>
                                        <li>Drag a piece off the board to remove it</li>
                                        <li>Use "↶ Undo" to undo placements</li>
                                        <li>Click "Hide Tray" when done setting up</li>
                                    </ul>
                                </div>
                            </div>
                        )}

                        {/* Piece Picker for Custom Position Setup - OLD METHOD (Hidden when tray is active) */}
                        {mode === TEACHING_MODE && showPiecePicker && !isPieceSetupMode && (
                            <div className="mt-3 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                                <div className="flex items-center justify-between mb-3">
                                    <h5 className="text-sm font-bold text-blue-900">🎨 Add Individual Pieces</h5>
                                    <button
                                        onClick={() => {
                                            setShowPiecePicker(false);
                                            setSelectedPieceType(null);
                                        }}
                                        className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-gray-700"
                                    >
                                        Hide
                                    </button>
                                </div>
                                <div className="mb-3 p-3 bg-white border border-blue-200 rounded-lg">
                                    <p className="text-xs text-blue-900 font-semibold mb-1">📋 How to use:</p>
                                    <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                                        <li>Use "Clear Board" above to start with an empty board</li>
                                        <li>Click a piece type below to select it{selectedPieceType && <span className="ml-1 font-semibold">(Currently: {selectedPieceType})</span>}</li>
                                        <li>Drag the selected piece from the sidelines onto any square</li>
                                        <li>Drag existing pieces on the board to move or remove them</li>
                                        <li>Right-click any piece to remove it from the board</li>
                                    </ul>
                                </div>
                                
                                {/* White Pieces */}
                                <div className="mb-3">
                                    <p className="text-xs font-semibold text-gray-700 mb-2">White Pieces:</p>
                                    <div className="flex gap-2 flex-wrap">
                                        {[
                                            { symbol: '♔', type: 'K', name: 'King' },
                                            { symbol: '♕', type: 'Q', name: 'Queen' },
                                            { symbol: '♖', type: 'R', name: 'Rook' },
                                            { symbol: '♗', type: 'B', name: 'Bishop' },
                                            { symbol: '♘', type: 'N', name: 'Knight' },
                                            { symbol: '♙', type: 'P', name: 'Pawn' }
                                        ].map(piece => (
                                            <button
                                                key={`w-${piece.type}`}
                                                onClick={() => {
                                                    setSelectedPieceType(`White ${piece.name}`);
                                                    const newGame = new Chess(game.fen());
                                                    // Add piece to a1 square temporarily so user can drag it
                                                    try {
                                                        newGame.put({ type: piece.type.toLowerCase() as any, color: 'w' }, 'a1' as any);
                                                        setGame(newGame);
                                                        toast.success(`White ${piece.name} added to a1. Drag it to desired square!`);
                                                    } catch (error) {
                                                        // If a1 is occupied, try other squares
                                                        const emptySquares = ['a1', 'h1', 'a8', 'h8', 'd4', 'e4'];
                                                        for (const sq of emptySquares) {
                                                            try {
                                                                const testGame = new Chess(game.fen());
                                                                testGame.put({ type: piece.type.toLowerCase() as any, color: 'w' }, sq as any);
                                                                setGame(testGame);
                                                                toast.success(`White ${piece.name} added to ${sq}. Drag it to desired square!`);
                                                                break;
                                                            } catch (e) {
                                                                // Continue to next square
                                                                console.log('Square occupied, trying next:', e);
                                                            }
                                                        }
                                                    }
                                                }}
                                                className={`px-3 py-2 hover:bg-gray-100 border-2 hover:border-blue-500 rounded-lg text-2xl transition-colors ${
                                                    selectedPieceType === `White ${piece.name}` 
                                                        ? 'bg-blue-200 border-blue-600' 
                                                        : 'bg-white border-gray-300'
                                                }`}
                                                title={`White ${piece.name}`}
                                            >
                                                {piece.symbol}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Black Pieces */}
                                <div>
                                    <p className="text-xs font-semibold text-gray-700 mb-2">Black Pieces:</p>
                                    <div className="flex gap-2 flex-wrap">
                                        {[
                                            { symbol: '♚', type: 'k', name: 'King' },
                                            { symbol: '♛', type: 'q', name: 'Queen' },
                                            { symbol: '♜', type: 'r', name: 'Rook' },
                                            { symbol: '♝', type: 'b', name: 'Bishop' },
                                            { symbol: '♞', type: 'n', name: 'Knight' },
                                            { symbol: '♟', type: 'p', name: 'Pawn' }
                                        ].map(piece => (
                                            <button
                                                key={`b-${piece.type}`}
                                                onClick={() => {
                                                    setSelectedPieceType(`Black ${piece.name}`);
                                                    const newGame = new Chess(game.fen());
                                                    // Add piece to a8 square temporarily so user can drag it
                                                    try {
                                                        newGame.put({ type: piece.type.toLowerCase() as any, color: 'b' }, 'a8' as any);
                                                        setGame(newGame);
                                                        toast.success(`Black ${piece.name} added to a8. Drag it to desired square!`);
                                                    } catch (error) {
                                                        // If a8 is occupied, try other squares
                                                        const emptySquares = ['a8', 'h8', 'a1', 'h1', 'd5', 'e5'];
                                                        for (const sq of emptySquares) {
                                                            try {
                                                                const testGame = new Chess(game.fen());
                                                                testGame.put({ type: piece.type.toLowerCase() as any, color: 'b' }, sq as any);
                                                                setGame(testGame);
                                                                toast.success(`Black ${piece.name} added to ${sq}. Drag it to desired square!`);
                                                                break;
                                                            } catch (e) {
                                                                // Continue to next square
                                                                console.log('Square occupied, trying next:', e);
                                                            }
                                                        }
                                                    }
                                                }}
                                                className={`px-3 py-2 hover:bg-gray-100 border-2 hover:border-blue-500 rounded-lg text-2xl transition-colors ${
                                                    selectedPieceType === `Black ${piece.name}` 
                                                        ? 'bg-blue-200 border-blue-600' 
                                                        : 'bg-white border-gray-300'
                                                }`}
                                                title={`Black ${piece.name}`}
                                            >
                                                {piece.symbol}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-3 text-xs text-blue-700 bg-blue-100 p-2 rounded">
                                    💡 Tip: After setting up your position, you can save it as a custom puzzle or use "Load FEN Position" to save the position code.
                                </div>
                            </div>
                        )}

                        {/* Move History */}
                        {puzzleProgress.length > 0 && (
                            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs font-semibold text-gray-600 mb-1">Move History:</p>
                                <p className="text-sm text-gray-800">{puzzleProgress.join(' → ')}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sidebar - Puzzles & Controls - 1/4 width */}
                <div className="space-y-3 lg:col-span-1">
                    {/* Current Puzzle Info */}
                    {currentPuzzle && (
                        <div className="bg-white rounded-lg shadow-lg p-3">
                            <h4 className="font-semibold text-gray-800 mb-2">Current Puzzle</h4>
                            <div className="space-y-2 text-sm">
                                <p className="text-gray-600">{currentPuzzle.description}</p>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-gray-500">Rating:</span>
                                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                        {currentPuzzle.rating}
                                        </span>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {currentPuzzle.themes.map(theme => (
                                        <span key={theme} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                            {theme}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Mode Switcher - Lichess Style */}
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                            <div className="flex border-b border-gray-200 bg-gray-50">
                                <button
                                    onClick={() => {
                                        // Clean up existing state
                                        setMode('puzzle');
                                        setOriginalMode('puzzle'); // Update original mode when switching
                                        setShareMode('collaboration'); // Default to collaboration in puzzle mode
                                        setIsBoardShared(false); // Reset sharing state
                                        setStudentPositions(new Map());
                                        
                                        // Clear any existing metadata
                                        if (room && room.localParticipant) {
                                            try {
                                                room.localParticipant.setMetadata('');
                                                console.log('🧹 Cleared metadata when switching to puzzle mode');
                                            } catch (error) {
                                                console.warn('Could not clear metadata:', error);
                                            }
                                        }
                                        setIsPieceSetupMode(false);
                                        setSelectedTrayPiece(null);
                                        setShowPiecePicker(false);
                                        setRightClickedSquares({});
                                        setArrows([]);
                                        setArrowStart(null);
                                        setGame(new Chess()); // Reset to starting position when switching to puzzle mode
                                        toast.info('Switched to Puzzle Mode');
                                    }}
                                    className={`flex-1 px-4 py-3 text-sm font-semibold transition-all relative ${
                                        mode === PUZZLE_MODE
                                            ? 'bg-white text-gray-900 border-b-2 border-blue-600'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                    }`}
                                >
                                    Puzzle
                                    {mode === PUZZLE_MODE && (
                                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
                                    )}
                                </button>
                                {!disableTeachingMode && (
                                    <button
                                        onClick={switchToTeachingMode}
                                        className={`flex-1 px-4 py-3 text-sm font-semibold transition-all relative ${
                                            mode === 'teaching'
                                                ? 'bg-white text-gray-900 border-b-2 border-blue-600'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                        }`}
                                    >
                                        Teaching
                                        {mode === 'teaching' && (
                                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
                                        )}
                                    </button>
                                )}
                            </div>
                            <div className="p-4 bg-gray-50/30">
                                <p className="text-xs text-gray-500">
                                    {mode === 'puzzle' ? 'Students solve specific puzzles' : 'Demonstrate moves and concepts freely'}
                                </p>
                            </div>
                            
                            {/* Teaching Mode Controls - Lichess Style */}
                            {!disableTeachingMode && mode === 'teaching' && (
                                <div className="mt-4 space-y-4">
                                    {/* Interaction Modes - Like Lichess */}
                                    <div className="border border-gray-200 rounded-lg bg-white p-3">
                                        <h5 className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide">Interaction Mode</h5>
                                        <div className="grid grid-cols-3 gap-2">
                                            <button
                                                onClick={() => {
                                                    setInteractionMode('move');
                                                    setSelectedTrayPiece(null);
                                                    setArrowStart(null);
                                                    setSelectedSquares(new Set());
                                                }}
                                                className={`px-3 py-2 rounded text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                                                    interactionMode === 'move'
                                                        ? 'bg-blue-600 text-white shadow-sm'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                                title="Normal move mode - drag pieces to move them"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                                </svg>
                                                Move
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setInteractionMode('select');
                                                    setSelectedTrayPiece(null);
                                                    setArrowStart(null);
                                                    setDrawingMode('highlight');
                                                }}
                                                className={`px-3 py-2 rounded text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                                                    interactionMode === 'select'
                                                        ? 'bg-blue-600 text-white shadow-sm'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                                title="Select mode - click pieces to select/highlight them"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                Select
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setInteractionMode('delete');
                                                    setSelectedTrayPiece(null);
                                                    setArrowStart(null);
                                                    setSelectedSquares(new Set());
                                                }}
                                                className={`px-3 py-2 rounded text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                                                    interactionMode === 'delete'
                                                        ? 'bg-red-600 text-white shadow-sm'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                                title="Delete mode - click pieces to remove them"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                                Delete
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">
                                            {interactionMode === 'move' && 'Drag pieces to move them'}
                                            {interactionMode === 'select' && 'Click pieces to select and highlight them'}
                                            {interactionMode === 'delete' && 'Click pieces to remove them from the board'}
                                        </p>
                                    </div>

                                    {/* Board Controls - Like Lichess */}
                                    <div className="border border-gray-200 rounded-lg bg-white p-3">
                                        <h5 className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide">Board Actions</h5>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                onClick={() => setBoardOrientation(boardOrientation === 'white' ? 'black' : 'white')}
                                                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
                                                title="Flip board orientation"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Flip Board
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setGame(new Chess());
                                                    setTeachingModeHistory([]);
                                                    setPuzzleProgress([]);
                                                    toast.success('Reset to starting position');
                                                }}
                                                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
                                                title="Reset to starting position"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Reset Position
                                            </button>
                                            <button
                                                onClick={() => {
                                                    console.log('Clear Board clicked - Setting minimal valid position');
                                                    try {
                                                        const minimalValidFen = '4k3/8/8/8/8/8/8/4K3 w - - 0 1';
                                                        const emptyGame = new Chess(minimalValidFen);
                                                        setGame(emptyGame);
                                                        setTeachingModeHistory([]);
                                                        setPuzzleProgress([]);
                                                        setIsPieceSetupMode(true);
                                                        setShowPiecePicker(true);
                                                        toast.success('Board cleared! Click pieces in tray, then click board squares to place them');
                                                    } catch (error) {
                                                        console.error('Error clearing board:', error);
                                                        toast.error('Failed to clear board');
                                                    }
                                                }}
                                                className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded text-sm font-medium transition-colors flex items-center justify-center gap-1.5 border border-red-200"
                                                title="Clear all pieces from board"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                                Clear Board
                                            </button>
                                            <button
                                                onClick={teachingModeHistory.length > 0 ? () => {
                                                    undoTeachingMove();
                                                    toast.info('Move undone');
                                                } : undefined}
                                                disabled={teachingModeHistory.length === 0}
                                                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm font-medium transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="Undo last move"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                                </svg>
                                                Undo
                                            </button>
                                        </div>
                                    </div>

                                    {/* Annotations Section */}
                                    <div className="border border-gray-200 rounded-lg bg-white p-3">
                                        <h5 className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide">Annotations</h5>
                                        <div className="grid grid-cols-2 gap-2 mb-3">
                                    <button
                                        onClick={() => {
                                            setDrawingMode('highlight');
                                            setArrowStart(null);
                                                    setSelectedTrayPiece(null);
                                        }}
                                                className={`px-3 py-2 rounded text-sm font-medium transition-all ${
                                                    drawingMode === 'highlight' && !selectedTrayPiece
                                                        ? 'bg-blue-600 text-white shadow-sm'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        ⬛ Highlight
                                    </button>
                                    <button
                                        onClick={() => {
                                            setDrawingMode('arrow');
                                            setArrowStart(null);
                                                    setSelectedTrayPiece(null);
                                        }}
                                                className={`px-3 py-2 rounded text-sm font-medium transition-all ${
                                                    drawingMode === 'arrow' && !selectedTrayPiece
                                                        ? 'bg-blue-600 text-white shadow-sm'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        ➡️ Arrow
                                    </button>
                                </div>
                                        <div className="grid grid-cols-4 gap-2 mb-3">
                                    {[
                                        { color: 'green' as const, bg: 'bg-green-500' },
                                        { color: 'red' as const, bg: 'bg-red-500' },
                                        { color: 'blue' as const, bg: 'bg-blue-500' },
                                        { color: 'yellow' as const, bg: 'bg-yellow-400' }
                                    ].map(({ color, bg }) => (
                                        <button
                                            key={color}
                                            onClick={() => setDrawingColor(color)}
                                                    className={`h-8 rounded border-2 transition-all ${bg} ${
                                                        drawingColor === color ? 'border-gray-900 ring-2 ring-gray-400 shadow-md' : 'border-gray-300'
                                                    }`}
                                                    title={`${color.charAt(0).toUpperCase() + color.slice(1)} color`}
                                                >
                                                    {drawingColor === color && <span className="text-white text-xs font-bold">✓</span>}
                                        </button>
                                    ))}
                                </div>
                                        {arrowStart && (
                                            <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-900 text-center mb-2">
                                                Arrow start: <strong>{arrowStart.toUpperCase()}</strong>
                                            </div>
                                        )}
                            </div>

                                    {/* Piece Placement Section */}
                                    <div className="border border-gray-200 rounded-lg bg-white p-3">
                                        <h5 className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide">Place Pieces</h5>
                                        {selectedTrayPiece && (
                                            <div className="mb-3 bg-blue-50 border border-blue-200 rounded p-2 text-sm text-blue-900 text-center">
                                                Selected: <strong>{selectedTrayPiece.color === 'w' ? 'White' : 'Black'} {
                                                    { 'K': 'King', 'Q': 'Queen', 'R': 'Rook', 'B': 'Bishop', 'N': 'Knight', 'P': 'Pawn' }[selectedTrayPiece.piece]
                                                }</strong>
                        </div>
                    )}
                                        <div className="space-y-3">
                                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                                <div className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">White Pieces</div>
                                                <div className="grid grid-cols-6 gap-2">
                                                    {[
                                                        { piece: 'K', symbol: '♔' }, { piece: 'Q', symbol: '♕' }, { piece: 'R', symbol: '♖' },
                                                        { piece: 'B', symbol: '♗' }, { piece: 'N', symbol: '♘' }, { piece: 'P', symbol: '♙' }
                                                    ].map(({ piece, symbol }) => (
                                                        <button
                                                            key={`w-${piece}`}
                                                            onClick={() => {
                                                                setSelectedTrayPiece({ piece, color: 'w' });
                                                                setDrawingMode('highlight');
                                                                setArrowStart(null);
                                                            }}
                                                            className={`h-10 flex items-center justify-center text-2xl rounded transition-all border-2 ${
                                                                selectedTrayPiece?.piece === piece && selectedTrayPiece?.color === 'w'
                                                                    ? 'bg-blue-600 text-white border-blue-700 ring-2 ring-blue-400 shadow-md'
                                                                    : 'bg-white hover:bg-gray-100 border-gray-300 shadow-sm hover:shadow'
                                                            }`}
                                                            title={`White ${piece}`}
                                                        >
                                                            {symbol}
                                                        </button>
                                                    ))}
                                </div>
                                            </div>
                                            <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                                                <div className="text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wide">Black Pieces</div>
                                                <div className="grid grid-cols-6 gap-2">
                                                    {[
                                                        { piece: 'K', symbol: '♚' }, { piece: 'Q', symbol: '♛' }, { piece: 'R', symbol: '♜' },
                                                        { piece: 'B', symbol: '♝' }, { piece: 'N', symbol: '♞' }, { piece: 'P', symbol: '♟' }
                                                    ].map(({ piece, symbol }) => (
                                                        <button
                                                            key={`b-${piece}`}
                                                            onClick={() => {
                                                                setSelectedTrayPiece({ piece, color: 'b' });
                                                                setDrawingMode('highlight');
                                                                setArrowStart(null);
                                                            }}
                                                            className={`h-10 flex items-center justify-center text-2xl rounded transition-all border-2 ${
                                                                selectedTrayPiece?.piece === piece && selectedTrayPiece?.color === 'b'
                                                                    ? 'bg-blue-600 text-white border-blue-700 ring-2 ring-blue-400 shadow-md'
                                                                    : 'bg-gray-700 hover:bg-gray-600 border-gray-600 text-white shadow-sm hover:shadow'
                                                            }`}
                                                            title={`Black ${piece}`}
                                                        >
                                                            {symbol}
                                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                                    </div>
                                    
                                    {/* Quick Actions */}
                                    <div className="border border-gray-200 rounded-lg bg-white p-3">
                                        <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        setSelectedTrayPiece(null);
                                        setArrowStart(null);
                                                    setSelectedSquares(new Set());
                                                    // Clear highlights from selected squares
                                                    const newHighlights = { ...rightClickedSquares };
                                                    selectedSquares.forEach((square) => {
                                                        delete newHighlights[square];
                                                    });
                                                    setRightClickedSquares(newHighlights);
                                                    toast.info('Selection cleared');
                                                }}
                                                className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm font-medium transition-colors"
                                            >
                                                Clear Selection
                                </button>
                                <button
                                                onClick={() => {
                                                    clearAnnotations();
                                                    setSelectedSquares(new Set());
                                                    toast.success('All annotations and selections cleared');
                                                }}
                                                className="flex-1 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded text-sm font-medium transition-colors border border-red-200"
                                            >
                                                Clear Annotations
                                </button>
                            </div>
                                        <div className="mt-2 text-xs text-gray-500 bg-blue-50 rounded p-2 leading-relaxed border border-blue-100">
                                            💡 <strong>Tip:</strong> Right-click board squares to add {drawingMode === 'highlight' ? 'highlights' : 'arrows'}. Press ESC to cancel.
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                    {/* Puzzle Mode - Tabbed Interface - Lichess Style */}
                    {mode === 'puzzle' && (
                    <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                        {/* Tab Navigation - Lichess Style */}
                        <div className="flex border-b border-gray-200 bg-gray-50">
                            <button
                                onClick={() => setPuzzleModeTab('library')}
                                className={`flex-1 px-4 py-3 text-sm font-semibold transition-all relative ${
                                    puzzleModeTab === 'library'
                                        ? 'bg-white text-gray-900'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                }`}
                            >
                                Library
                                {puzzleModeTab === 'library' && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
                                )}
                            </button>
                            <button
                                onClick={() => setPuzzleModeTab('import')}
                                className={`flex-1 px-4 py-3 text-sm font-semibold transition-all relative ${
                                    puzzleModeTab === 'import'
                                        ? 'bg-white text-gray-900'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                }`}
                            >
                                Import
                                {puzzleModeTab === 'import' && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
                                )}
                            </button>
                            <button
                                onClick={() => setPuzzleModeTab('create')}
                                className={`flex-1 px-4 py-3 text-sm font-semibold transition-all relative ${
                                    puzzleModeTab === 'create'
                                        ? 'bg-white text-gray-900'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                }`}
                            >
                                Create
                                {puzzleModeTab === 'create' && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
                                )}
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="p-4 bg-gray-50/30">
                            {/* LIBRARY TAB */}
                            {puzzleModeTab === 'library' && (
                                <div className="space-y-3">
                                    {/* Compact Search */}
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="🔍 Search puzzles..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-purple-500 focus:outline-none"
                        />

                                    {/* Compact Filters */}
                                    <div className="grid grid-cols-2 gap-2">
                            <select
                                value={selectedComplexity}
                                onChange={(e) => setSelectedComplexity(e.target.value as any)}
                                            className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-purple-500 focus:outline-none"
                            >
                                <option value="all">All Levels</option>
                                <option value="beginner">🟢 Beginner</option>
                                <option value="intermediate">🟡 Intermediate</option>
                                <option value="advanced">🔴 Advanced</option>
                            </select>

                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value as any)}
                                            className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-purple-500 focus:outline-none"
                            >
                                <option value="all">All Categories</option>
                                <option value="tactics">⚔️ Tactics</option>
                                <option value="endgame">👑 Endgame</option>
                                            <option value="opening">📖 Opening</option>
                                <option value="middlegame">🎯 Middlegame</option>
                            </select>
                        </div>

                                    {/* Status Bar */}
                                    <div className="flex items-center justify-between text-xs py-2 px-3 bg-purple-50 rounded border border-purple-200">
                                        <span className="text-purple-700 font-semibold">
                                            {loadingPuzzles ? '⏳ Loading...' : `${filteredPuzzles.length} puzzles`}
                                        </span>
                                        <span className="text-purple-600">
                                            {bookmarkedPuzzleIds.length > 0 && `⭐ ${bookmarkedPuzzleIds.length}`}
                                        </span>
                        </div>

                                    {/* Puzzle Cards */}
                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                        {loadingPuzzles ? (
                                            <div className="text-center py-8 text-gray-500">
                                                <div className="animate-spin text-2xl mb-2">⏳</div>
                                                <p className="text-sm">Loading puzzles...</p>
                    </div>
                                        ) : filteredPuzzles.length === 0 ? (
                                            <div className="text-center py-8">
                                                <p className="text-sm text-gray-500 mb-3">No puzzles found</p>
                                                <button
                                                    onClick={() => setPuzzleModeTab('import')}
                                                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium"
                                                >
                                                    📥 Import Puzzles
                                                </button>
                                            </div>
                                        ) : (
                                            filteredPuzzles.map(puzzle => {
                                                const isBookmarked = bookmarkedPuzzleIds.includes(puzzle.id);
                                                
                                                return (
                                                    <div
                                                        key={puzzle.id}
                                                        className={`relative p-2.5 rounded-lg transition-all ${
                                                            currentPuzzle?.id === puzzle.id
                                                                ? 'bg-purple-100 border-2 border-purple-500 shadow-md'
                                                                : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent hover:border-purple-200'
                                                        }`}
                                                    >
                                                        <div className="flex justify-between items-start gap-2">
                                                            <div 
                                                                onClick={() => loadPuzzle(puzzle)}
                                                                className="flex-1 cursor-pointer"
                                                            >
                                                                <div className="flex items-center gap-1.5 mb-1">
                                                                    <span className={`text-xs ${
                                                                        puzzle.complexity === 'beginner' ? 'text-green-600' :
                                                                        puzzle.complexity === 'intermediate' ? 'text-yellow-600' :
                                                                        'text-red-600'
                                                                    }`}>
                                                                        {puzzle.complexity === 'beginner' ? '🟢' :
                                                                         puzzle.complexity === 'intermediate' ? '🟡' : '🔴'}
                                                                    </span>
                                                                    <p className="text-xs font-medium text-gray-800 flex-1 line-clamp-1">
                                                                        {puzzle.description}
                                                                    </p>
                                                                    {currentPuzzle?.id === puzzle.id && (
                                                                        <span className="text-purple-600 text-xs">✓</span>
                                                                    )}
                                                                </div>
                                                                <div className="flex flex-wrap gap-1">
                                                                    <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                                                                        {puzzle.rating}
                                                                    </span>
                                                                    {puzzle.themes.slice(0, 2).map(theme => (
                                                                        <span key={theme} className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                                                            {theme}
                                                                        </span>
                                                                    ))}
                                                                    {puzzle.source === 'lichess' && (
                                                                        <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                                                            Lichess
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Bookmark Button */}
                                <button
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();
                                                                    if (isBookmarked) {
                                                                        await removeBookmark('service-provider-id', puzzle.id, participantName);
                                                                        setBookmarkedPuzzleIds(prev => prev.filter(id => id !== puzzle.id));
                                                                    } else {
                                                                        await bookmarkPuzzle('service-provider-id', puzzle.id, participantName, subscriptionId);
                                                                        setBookmarkedPuzzleIds(prev => [...prev, puzzle.id]);
                                                                    }
                                                                }}
                                                                className={`px-2 py-1 rounded text-sm transition-all ${
                                                                    isBookmarked 
                                                                        ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200' 
                                                                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-yellow-500'
                                                                }`}
                                                                title={isBookmarked ? 'Remove bookmark' : 'Bookmark puzzle'}
                                                            >
                                                                ⭐
                                </button>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* IMPORT TAB */}
                            {puzzleModeTab === 'import' && (
                                <div className="space-y-2.5">
                                    {/* CSV Upload - Primary Method */}
                                    <div className="p-3 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border-2 border-green-400">
                                        <p className="text-xs font-bold text-green-900 mb-1.5">📊 CSV Bulk Import (Recommended)</p>
                                        <p className="text-[10px] text-green-700 mb-2">Upload Lichess database with 3M+ puzzles</p>
                                <input
                                            type="file"
                                            accept=".csv"
                                            onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    try {
                                                        toast.info('Importing puzzles from CSV... This may take a minute.');
                                                        const { importLichessCSVFile } = await import('../../../../../utils/importLichessCSV');
                                                        await importLichessCSVFile(file, 'service-provider-id', participantName, 1000);
                                                        setPuzzleModeTab('library');
                                                    } catch (error) {
                                                        console.error(error);
                                                        toast.error('CSV import failed');
                                                    }
                                                }
                                            }}
                                            className="block w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-green-600 file:text-white hover:file:bg-green-700"
                                        />
                                        <div className="mt-2 space-y-1">
                                            <a
                                                href="https://database.lichess.org/#puzzles"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                                className="block text-xs text-green-800 hover:underline font-semibold"
                                            >
                                                📥 Download Lichess Database →
                                            </a>
                                            <p className="text-[10px] text-green-700">
                                                File: <code className="bg-white px-1 rounded">lichess_db_puzzle.csv.zst</code>
                                    </p>
                                </div>
                            </div>

                                    {/* FEN Position */}
                                    <div className="p-3 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg border border-orange-300">
                                        <p className="text-xs font-semibold text-orange-900 mb-1.5">📝 Custom Position (FEN)</p>
                                        <p className="text-[10px] text-orange-700 mb-2">Load any chess position</p>
                                        {!showFenInput ? (
                                <button
                                                onClick={() => setShowFenInput(true)}
                                                className="w-full px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded font-medium"
                                >
                                                Load FEN Position
                                </button>
                                        ) : (
                                            <div className="space-y-2">
                                                <textarea
                                                    value={customFen}
                                                    onChange={(e) => setCustomFen(e.target.value)}
                                                    placeholder="rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
                                                    className="w-full p-2 border rounded text-[10px] font-mono resize-none"
                                                    rows={2}
                                                />
                                                <div className="flex gap-1.5">
                                                    <button
                                                        onClick={loadCustomPosition}
                                                        className="flex-1 px-2 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium"
                                                    >
                                                        ✓ Load
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(game.fen());
                                                            toast.success('FEN copied!');
                                                        }}
                                                        className="flex-1 px-2 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium"
                                                    >
                                                        📋 Copy
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setShowFenInput(false);
                                                            setCustomFen('');
                                                        }}
                                                        className="px-2 py-1.5 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded text-xs"
                                                    >
                                                        ✖
                                                    </button>
                                                </div>
                                                <p className="text-[10px] text-orange-700">
                                                    Get FEN from <a href="https://lichess.org/editor" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Lichess Editor</a>
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Help Section */}
                                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                        <p className="text-xs font-semibold text-blue-900 mb-1">📚 How to Get Puzzles:</p>
                                        <ul className="text-[10px] text-blue-800 space-y-1 list-disc list-inside">
                                            <li><strong>CSV Import:</strong> Best for bulk (1000+ puzzles)</li>
                                            <li><strong>FEN:</strong> Create custom positions</li>
                                            <li><strong>Create Tab:</strong> Save current board setup</li>
                                            <li><strong>Sample Puzzles:</strong> 8 puzzles included by default</li>
                                        </ul>
                                        <div className="mt-2 pt-2 border-t border-blue-200">
                                            <a 
                                                href="https://database.lichess.org/#puzzles" 
                                    target="_blank"
                                    rel="noopener noreferrer"
                                                className="text-xs text-blue-700 hover:text-blue-900 underline font-semibold"
                                            >
                                                → Download 3M+ Puzzles (CSV)
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* CREATE TAB */}
                            {puzzleModeTab === 'create' && (
                                <div className="space-y-2.5">
                                    {!showPuzzleCreator ? (
                                        <div className="text-center py-8">
                                            <p className="text-xs text-gray-600 mb-3">
                                                Set up a position on the board,<br/>then save it as a puzzle
                                            </p>
                                            <button
                                                onClick={() => setShowPuzzleCreator(true)}
                                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium"
                                            >
                                                ✨ Start Creating
                                            </button>
                                </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <input
                                                type="text"
                                                value={newPuzzle.description}
                                                onChange={(e) => setNewPuzzle({...newPuzzle, description: e.target.value})}
                                                placeholder="Description *"
                                                className="w-full px-3 py-2 border rounded-lg text-sm"
                                            />
                                            <input
                                                type="text"
                                                value={newPuzzle.solution}
                                                onChange={(e) => setNewPuzzle({...newPuzzle, solution: e.target.value})}
                                                placeholder="Solution moves (e.g., e4 e5 Nf3) *"
                                                className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                                            />
                                            <textarea
                                                value={newPuzzle.hint}
                                                onChange={(e) => setNewPuzzle({...newPuzzle, hint: e.target.value})}
                                                placeholder="Hint (optional)"
                                                className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
                                                rows={2}
                                            />
                                            <div className="grid grid-cols-2 gap-2">
                                                <select
                                                    value={newPuzzle.complexity}
                                                    onChange={(e) => setNewPuzzle({...newPuzzle, complexity: e.target.value as any})}
                                                    className="px-2 py-1.5 border rounded text-xs"
                                                >
                                                    <option value="beginner">🟢 Beginner</option>
                                                    <option value="intermediate">🟡 Intermediate</option>
                                                    <option value="advanced">🔴 Advanced</option>
                                                </select>
                                                <select
                                                    value={newPuzzle.category}
                                                    onChange={(e) => setNewPuzzle({...newPuzzle, category: e.target.value as any})}
                                                    className="px-2 py-1.5 border rounded text-xs"
                                                >
                                                    <option value="tactics">⚔️ Tactics</option>
                                                    <option value="endgame">👑 Endgame</option>
                                                    <option value="opening">📖 Opening</option>
                                                    <option value="middlegame">🎯 Middlegame</option>
                                                </select>
                                            </div>
                                            <input
                                                type="text"
                                                value={newPuzzle.themes}
                                                onChange={(e) => setNewPuzzle({...newPuzzle, themes: e.target.value})}
                                                placeholder="Themes (e.g., fork, pin)"
                                                className="w-full px-3 py-2 border rounded-lg text-sm"
                                            />
                                            <div className="flex gap-2 pt-2">
                                                <button
                                                    onClick={createCustomPuzzle}
                                                    className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
                                                >
                                                    ✓ Save
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setShowPuzzleCreator(false);
                                                        setNewPuzzle({
                                                            description: '',
                                                            hint: '',
                                                            solution: '',
                                                            complexity: 'beginner',
                                                            category: 'tactics',
                                                            themes: ''
                                                        });
                                                    }}
                                                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg font-medium"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            </div>
                        </div>
                    )}

                    {/* Annotations in Puzzle Mode */}
                    {mode === 'puzzle' && (
                        <div className="bg-white rounded-lg shadow-lg p-3">
                            <h4 className="font-semibold text-gray-800 text-sm mb-2">✏️ Annotations</h4>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                                <button
                                    onClick={() => {
                                        setDrawingMode('highlight');
                                        setArrowStart(null);
                                    }}
                                    className={`px-2 py-1.5 rounded text-xs font-medium ${
                                        drawingMode === 'highlight' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'
                                    }`}
                                >
                                    ⬛ Highlight
                                </button>
                                <button
                                    onClick={() => {
                                        setDrawingMode('arrow');
                                        setArrowStart(null);
                                    }}
                                    className={`px-2 py-1.5 rounded text-xs font-medium ${
                                        drawingMode === 'arrow' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'
                                    }`}
                                >
                                    ➡️ Arrow
                                </button>
                            </div>
                            <div className="grid grid-cols-4 gap-1">
                                {[
                                    { color: 'green' as const, bg: 'bg-green-500' },
                                    { color: 'red' as const, bg: 'bg-red-500' },
                                    { color: 'blue' as const, bg: 'bg-blue-500' },
                                    { color: 'yellow' as const, bg: 'bg-yellow-400' }
                                ].map(({ color, bg }) => (
                                    <button
                                        key={color}
                                        onClick={() => setDrawingColor(color)}
                                        className={`h-7 rounded border-2 ${bg} ${
                                            drawingColor === color ? 'border-gray-900 ring-1 ring-gray-400' : 'border-gray-300'
                                        }`}
                                    >
                                        {drawingColor === color && <span className="text-white text-xs font-bold">✓</span>}
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={clearAnnotations}
                                className="w-full mt-2 text-xs px-2 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded font-medium"
                            >
                                🗑️ Clear All
                            </button>
                        </div>
                    )}

                    {/* REMOVE DUPLICATE CREATE SECTION */}
                    {false && mode === 'puzzle' && (
                        <div className="bg-white rounded-lg shadow-lg p-4 border-2 border-blue-200">
                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                ✨ Create Custom Puzzle
                            </h4>
                            {!showPuzzleCreator ? (
                                <div className="space-y-2">
                                    <p className="text-xs text-gray-600 mb-2">
                                        Set up a position on the board, then create a puzzle from it
                                    </p>
                                    <button
                                        onClick={() => setShowPuzzleCreator(true)}
                                        className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                                    >
                                        ➕ Create from Current Position
                                    </button>
                                </div>
                            ) : (
                            <div className="space-y-3">
                                <div>
                                    <label className="text-xs font-medium text-gray-700 mb-1 block">Description *</label>
                                    <input
                                        type="text"
                                        value={newPuzzle.description}
                                        onChange={(e) => setNewPuzzle({...newPuzzle, description: e.target.value})}
                                        placeholder="e.g., 'Find the checkmate in 2'"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="text-xs font-medium text-gray-700 mb-1 block">Solution Moves * <span className="text-gray-500 font-normal">(space-separated)</span></label>
                                    <input
                                        type="text"
                                        value={newPuzzle.solution}
                                        onChange={(e) => setNewPuzzle({...newPuzzle, solution: e.target.value})}
                                        placeholder="e.g., 'Qh7 Kf8 Qh8' or 'e2e4 e7e5'"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-blue-500 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="text-xs font-medium text-gray-700 mb-1 block">Hint (optional)</label>
                                    <input
                                        type="text"
                                        value={newPuzzle.hint}
                                        onChange={(e) => setNewPuzzle({...newPuzzle, hint: e.target.value})}
                                        placeholder="e.g., 'Look for a forcing move'"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-xs font-medium text-gray-700 mb-1 block">Complexity</label>
                                        <select
                                            value={newPuzzle.complexity}
                                            onChange={(e) => setNewPuzzle({...newPuzzle, complexity: e.target.value as any})}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                                        >
                                            <option value="beginner">🟢 Beginner</option>
                                            <option value="intermediate">🟡 Intermediate</option>
                                            <option value="advanced">🔴 Advanced</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-gray-700 mb-1 block">Category</label>
                                        <select
                                            value={newPuzzle.category}
                                            onChange={(e) => setNewPuzzle({...newPuzzle, category: e.target.value as any})}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                                        >
                                            <option value="tactics">⚔️ Tactics</option>
                                            <option value="endgame">👑 Endgame</option>
                                            <option value="opening">🌅 Opening</option>
                                            <option value="middlegame">🎯 Middlegame</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs font-medium text-gray-700 mb-1 block">Themes <span className="text-gray-500 font-normal">(comma-separated, optional)</span></label>
                                    <input
                                        type="text"
                                        value={newPuzzle.themes}
                                        onChange={(e) => setNewPuzzle({...newPuzzle, themes: e.target.value})}
                                        placeholder="e.g., 'fork, pin, checkmate'"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                                    />
                                </div>

                                <div className="pt-2 border-t border-gray-200">
                                    <p className="text-xs text-gray-600 mb-2">
                                        Current position: <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">{game.fen().substring(0, 30)}...</code>
                                    </p>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={createCustomPuzzle}
                                            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                                        >
                                            ✅ Create Puzzle
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowPuzzleCreator(false);
                                                setNewPuzzle({
                                                    description: '',
                                                    hint: '',
                                                    solution: '',
                                                    complexity: 'beginner',
                                                    category: 'tactics',
                                                    themes: ''
                                                });
                                            }}
                                            className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg text-sm font-medium transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        </div>
                    )}

                    {/* REMOVE DUPLICATE BULK IMPORT SECTION */}
                    {false && mode === 'puzzle' && (
                        <div className="bg-white rounded-lg shadow-lg p-3 border-2 border-orange-200">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="font-semibold text-gray-800 text-sm">📦 Bulk Import Puzzles</h4>
                                <button
                                    onClick={() => setShowBulkImport(!showBulkImport)}
                                    className="text-xs px-2 py-1 bg-orange-100 hover:bg-orange-200 rounded"
                                >
                                    {showBulkImport ? 'Hide' : 'Show'}
                                </button>
                            </div>
                            
                            {showBulkImport && (
                                <div className="space-y-3">
                                    {/* Quick Import 1000+ */}
                                    <div className="p-3 bg-blue-50 rounded border">
                                        <p className="text-xs text-blue-800 mb-2">
                                            <strong>Quick Import:</strong> Auto-import 1000+ puzzles from Lichess API
                                        </p>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    toast.info('Starting bulk import... This may take a few minutes');
                                                    const { build1000PuzzleDatabase } = await import('../../../../../utils/chessPuzzleImporter');
                                                    await build1000PuzzleDatabase('service-provider-id', participantName);
                                                } catch (error) {
                                                    console.error('Bulk import error:', error);
                                                    toast.error('Failed to import puzzles');
                                                }
                                            }}
                                            className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
                                        >
                                            🚀 Import 1000+ Puzzles
                                        </button>
                                    </div>

                                    {/* CSV Upload */}
                                    <div className="p-3 bg-green-50 rounded border">
                                        <p className="text-xs text-green-800 mb-2">
                                            <strong>CSV Import:</strong> Upload Lichess puzzle database
                                        </p>
                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    try {
                                                        toast.info('Importing puzzles from CSV...');
                                                        const { importLichessCSVFile } = await import('../../../../../utils/importLichessCSV');
                                                        await importLichessCSVFile(file, 'service-provider-id', participantName, 1000);
                                                    } catch (error) {
                                                        console.error('CSV import error:', error);
                                                        toast.error('Failed to import CSV');
                                                    }
                                                }
                                            }}
                                            className="block w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:bg-green-600 file:text-white hover:file:bg-green-700"
                                        />
                                        <a
                                            href="https://database.lichess.org/#puzzles"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block text-xs text-green-700 hover:underline mt-1"
                                        >
                                            📥 Download Lichess CSV
                                        </a>
                                    </div>

                                    <div className="text-xs text-gray-600 bg-gray-50 rounded p-2">
                                        💡 <strong>Tip:</strong> Download the Lichess puzzle database (3M+ puzzles) and upload the CSV file here, or use quick import for instant setup.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* REMOVE DUPLICATE PUZZLE LIBRARY SECTION - Now in Library Tab */}
                    {false && mode === 'puzzle' && (
                    <div className="bg-white rounded-lg shadow-lg p-3">
                        <h4 className="font-semibold text-gray-800 mb-2 text-sm">Puzzle Library ({filteredPuzzles.length})</h4>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {filteredPuzzles.length === 0 ? (
                                <p className="text-center text-gray-500 text-sm py-4">
                                    No puzzles match your filters
                                </p>
                            ) : (
                                filteredPuzzles.map(puzzle => (
                                    <div
                                        key={puzzle.id}
                                        className={`relative w-full text-left p-3 rounded-lg transition-colors ${
                                            currentPuzzle?.id === puzzle.id
                                                ? 'bg-purple-100 border-2 border-purple-500'
                                                : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                                        }`}
                                    >
                                        <div 
                                            onClick={() => loadPuzzle(puzzle)}
                                            className="cursor-pointer"
                                    >
                                        <div className="flex justify-between items-start mb-1">
                                            <div className="flex items-center gap-1 flex-1">
                                                <span className={`text-xs ${
                                                    puzzle.complexity === 'beginner' ? 'text-green-600' :
                                                    puzzle.complexity === 'intermediate' ? 'text-yellow-600' :
                                                    'text-red-600'
                                                }`}>
                                                    {puzzle.complexity === 'beginner' ? '🟢' :
                                                     puzzle.complexity === 'intermediate' ? '🟡' : '🔴'}
                                                </span>
                                                <p className="text-sm font-medium text-gray-800">
                                                    {puzzle.description}
                                                </p>
                                                    {puzzle.source === 'custom' && (
                                                        <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded ml-1">
                                                            ✨ Custom
                                                        </span>
                                                    )}
                                                    {puzzle.source === 'lichess' && (
                                                        <span className="text-xs bg-green-500 text-white px-1.5 py-0.5 rounded ml-1">
                                                            📥 Lichess
                                                        </span>
                                                    )}
                                            </div>
                                                <div className="flex items-center gap-1">
                                            {currentPuzzle?.id === puzzle.id && (
                                                <span className="text-purple-600">✓</span>
                                            )}
                                                    {puzzle.source === 'custom' && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (confirm('Delete this custom puzzle?')) {
                                                                    deleteCustomPuzzle(puzzle.id);
                                                                }
                                                            }}
                                                            className="ml-2 text-red-500 hover:text-red-700 p-1"
                                                            title="Delete custom puzzle"
                                                        >
                                                            🗑️
                                                        </button>
                                                    )}
                                                </div>
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                                {puzzle.rating}
                                            </span>
                                            {puzzle.themes.slice(0, 2).map(theme => (
                                                <span key={theme} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                                    {theme}
                                                </span>
                                            ))}
                                        </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    )}

                    {/* Student Positions Viewer (Teacher Only - In Meeting) */}
                    {userRole === 'teacher' && isInMeeting && isBoardShared && studentPositions.size > 0 && (
                        <div className="bg-white rounded-lg shadow-lg p-4 border-2 border-green-500">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                    Student Positions ({studentPositions.size})
                                </h4>
                            </div>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {Array.from(studentPositions.values())
                                    .sort((a, b) => b.timestamp - a.timestamp)
                                    .map((student, idx) => (
                                        <div 
                                            key={student.studentName}
                                            className="p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200"
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                                        {idx + 1}
                                                    </span>
                                                    <span className="font-medium text-gray-800 text-sm">
                                                        {student.studentName}
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        const newGame = new Chess(student.fen);
                                                        setGame(newGame);
                                                        toast.info(`Viewing ${student.studentName}'s position`);
                                                    }}
                                                    className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors"
                                                >
                                                    👁️ View
                                                </button>
                                            </div>
                                            <div className="text-xs text-gray-600">
                                                <span className="font-medium">Last move:</span> {student.lastMove}
                                            </div>
                                            <div className="text-[10px] text-gray-500 mt-1">
                                                {new Date(student.timestamp).toLocaleTimeString()}
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    )}

                    {/* REMOVE DUPLICATE CUSTOM POSITION SECTION - Now in Import Tab */}
                    {false && (
                        <div className="bg-white rounded-lg shadow-lg p-4">
                            <h4 className="font-semibold text-gray-800 mb-3">Custom Position</h4>
                            {!showFenInput ? (
                                <button
                                    onClick={() => setShowFenInput(true)}
                                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                                >
                                    📝 Load FEN Position
                                </button>
                            ) : (
                                <div className="space-y-2">
                                    <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                        <strong>What is FEN?</strong> FEN (Forsyth-Edwards Notation) describes a chess position.
                                        <br />
                                        <strong>Example:</strong> <code className="bg-white px-1 rounded text-[10px]">r1bqkbnr/pppp1ppp/2n5/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq - 2 3</code>
                                    </div>
                                    <textarea
                                        value={customFen}
                                        onChange={(e) => setCustomFen(e.target.value)}
                                        placeholder="rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
                                        className="w-full p-2 border border-gray-300 rounded-lg text-sm font-mono resize-none"
                                        rows={2}
                                    />
                                    <div className="text-xs text-gray-500 -mt-1">
                                        💡 Tip: Get FEN from <a href="https://lichess.org/editor" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Lichess Board Editor</a> or copy current position after setup
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={loadCustomPosition}
                                            className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                                        >
                                            ✓ Load
                                        </button>
                                        <button
                                            onClick={() => {
                                                const currentFen = game.fen();
                                                navigator.clipboard.writeText(currentFen);
                                                toast.success('Current position FEN copied to clipboard!');
                                            }}
                                            className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                                            title="Copy current board position as FEN"
                                        >
                                            📋 Copy Current
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowFenInput(false);
                                                setCustomFen('');
                                            }}
                                            className="px-3 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg text-sm font-medium transition-colors"
                                        >
                                            ✖
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* LiveKit Integration Status */}
                    {isInMeeting ? (
                        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border-2 border-green-500">
                            <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                                ✅ Live Collaboration Active
                            </h4>
                            <ul className="text-xs text-green-700 space-y-1">
                                {userRole === 'teacher' ? (
                                    <>
                                        <li>✅ Share puzzles with students</li>
                                        <li>✅ See all students' positions</li>
                                        <li>✅ Real-time move tracking</li>
                                        <li>✅ Click "View" to inspect student boards</li>
                                    </>
                                ) : (
                                    <>
                                        <li>✅ Receive puzzles from teacher</li>
                                        <li>✅ Your moves shared with teacher</li>
                                        <li>✅ Real-time collaboration</li>
                                        <li>✅ Solve puzzles together!</li>
                                    </>
                                )}
                            </ul>
                        </div>
                    ) : (
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border-2 border-purple-200">
                            <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                                💡 How to Use
                            </h4>
                            <ul className="text-xs text-purple-700 space-y-1">
                                <li>• Join a Live Class meeting</li>
                                <li>• Open Chequered tab during meeting</li>
                                <li>• Teachers: Share board with "Share Board" button</li>
                                <li>• Students: Solve puzzles, teacher sees all moves</li>
                                <li>• Coming: Lichess API & custom puzzles</li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* Info Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                    <span className="text-2xl">ℹ️</span>
                    <div>
                        <h4 className="font-semibold text-blue-900 mb-1">How to Use</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                            <li>• <strong>Students:</strong> Drag and drop pieces to solve the puzzle. Look for the best move!</li>
                            <li>• <strong>Teachers:</strong> Load custom positions using FEN notation to create your own exercises.</li>
                            <li>• <strong>Collaborative:</strong> Soon you'll be able to share this board during live classes!</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChequeredChess;
